import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, firstValueFrom, Subject, of, throwError } from 'rxjs';
import { concatMap, catchError, debounceTime, distinctUntilChanged, map, timeout } from 'rxjs/operators';
import { Course } from '../../model/course';
import { Question } from '../../model/question';
import { Subject as SubjectModel } from '../../model/subject';
import { Topic } from '../../model/topic';
import { LoggerService } from '../utils/logger.service';
import { User } from '../../model/user';
import { AuthService } from '../auth/auth.service';
import { RuntimeConfigService } from '../config/runtime-config.service';
import { BRAND } from '../brand/brand';
import { DASHBOARD_COURSE_STORAGE_SUFFIX } from '../../dashboard/dashboard-course-preference';
import { StudySessionStore } from './study-session.store';
import type { Id2Question, RecreateSettings } from './rest.types';
export type { Id2Question, RecreateSettings } from './rest.types';
import { Router } from '@angular/router';
import { getJsonNative, postJsonNative } from '../platform/native-http';
import { needsNativeDiscoveryFetch } from '../../oidc-discovery.loader';
import { isCapacitorNative } from '../platform/capacitor-platform';
import { ProgressSyncService } from '../sync/progress-sync.service';
import type { SyncPullResponse, SyncPushResponse } from '../sync/sync-event.types';
import { ContentPackService } from '../content-pack/content-pack.service';
import { LocalDatabaseService } from '../storage/local-database.service';
import { ConnectivityService } from '../connectivity/connectivity.service';
import { LocalEntitlementService } from '../entitlement/local-entitlement.service';
import { DeviceIdService } from '../device/device-id.service';

export interface LoginRequest {
  username: string;
  password: string;
}

export interface LoginResponse {
  sessionId: string;
  userId: number;
  admin?: boolean;
  isExternal: boolean;
  username?: string;
  email?: string;
}

/** Supabase Auth token endpoint payload (subset used by the app). */
export interface SupabaseTokenSet {
  accessToken: string;
  refreshToken: string | null;
  /** Epoch ms when the access token expires (best-effort; based on `expires_in` if present). */
  expiresAt: number | null;
}

export interface PolicyAcceptance {
  termsVersion: string;
  privacyVersion: string;
  acceptedAt: string;
}

export interface CurrentTermsStatus {
  policyVersion: string;
  policyUrl: string;
  accepted: boolean;
}

export interface PrivacyExportJob {
  jobId: string;
  status: 'queued' | 'processing' | 'ready' | 'failed' | 'expired';
  formatVersion: string;
  requestedAt: string;
  expiresAt: string;
  statusUrl: string;
  downloadUrl: string | null;
  sizeBytes: number | null;
  failureReason: string | null;
}

export interface QuestionsMetaRequest {
  courseId: number;
  subjectIds: number[];
}

export interface QuestionsMetaResponse {
  questionIds: number[];
}

export interface TopicCountResponse {
  topicId: number;
  count: number;
}

export interface QuestionSearchResponse {
  id: number;
  questionHtml: string;
  answer1: string | null;
  answer2: string | null;
  answer3: string | null;
  answer4: string | null;
  correctAnswer?: string | null;
  explanationHtml: string | null;
  subjectName: string;
  // Optional user-specific metadata (Flagged Questions)
  userFlagId?: number | null;
  userHide?: number | null;
  userHasNotes?: boolean | null;
}

export interface QuestionFlagResponse {
  id: number;
  name: string;
}

export interface UserSubscriptionResponse {
  id: number;
  subscriptionId: number;
  subscriptionName: string | null;
  courseId: number | null;
  courseName: string | null;
  validFrom: string | null;
  validTo: string | null;
  status: 'active' | 'upcoming' | 'expired' | 'unknown' | string;
  daysRemaining: number | null;
  trialStatus?: 'active' | 'expired' | string | null;
}

export interface AccessDeniedResponse {
  allowed: false;
  reason: 'SUBSCRIPTION_REQUIRED' | 'TRIAL_EXPIRED' | 'DAILY_LIMIT_REACHED' | 'TOTAL_LIMIT_REACHED' | 'PROFILE_REQUIRED' | 'NO_ACCESS_MAPPING' | string;
  courseId?: number | null;
  subjectId?: number | null;
  subscriptionId?: number | null;
  entitlementId?: string | null;
  detail?: string | null;
}

export interface QuestionsRequest {
  courseId: number;
  subjectIds: number[];
  questionIds?: number[];
}

@Injectable({
  providedIn: 'root',
})
export class RestService {
  /** Resolved on each read so APP_INITIALIZER can load runtime-config before first API call. */
  private get apiUrl(): string {
    return this.runtimeConfig.apiBaseUrl;
  }

  /** Base URL for the API (e.g. for admin dashboard links). */
  getApiUrl(): string {
    return this.apiUrl;
  }

  // Background queues for question tracking
  private seenQuestionsQueue$ = new Subject<{userId: number, questionId: number}>();
  private answerQueue$ = new Subject<QuestionAnswerEvent>();
  private seenQuestionsSub: any;
  private answerQueueSub: any;
  /** Resolve when flush sentinel is processed (queue drained). */
  private answerQueueDrainResolve: (() => void) | null = null;
  /** Sentinel: questionId 0 and userTestId 0 means "flush complete" marker. */
  private static readonly FLUSH_SENTINEL: QuestionAnswerEvent = {
    userTestId: 0,
    questionId: 0,
    selectedOption: null,
    correct: false,
    examMode: false,
    answeredAt: '',
  };

  lastAccessDenial: AccessDeniedResponse | null = null;

  get timeProgressExam(): number {
    return this.session.timeProgressExam;
  }
  set timeProgressExam(value: number) {
    this.session.timeProgressExam = value;
  }

  get questionIds(): number[] {
    return this.session.questionIds;
  }
  set questionIds(value: number[]) {
    this.session.questionIds = value;
  }

  get courseId(): number {
    return this.session.courseId;
  }
  set courseId(value: number) {
    this.session.courseId = value;
  }

  get subjectIds(): number[] {
    return this.session.subjectIds;
  }
  set subjectIds(value: number[]) {
    this.session.subjectIds = value;
  }

  get maxQuestions(): number {
    return this.session.maxQuestions;
  }
  set maxQuestions(value: number) {
    this.session.maxQuestions = value;
  }

  get currentUserTestId(): number | null {
    return this.session.currentUserTestId;
  }
  set currentUserTestId(value: number | null) {
    this.session.currentUserTestId = value;
  }

  get currentUserTestMode(): string | null {
    return this.session.currentUserTestMode;
  }
  set currentUserTestMode(value: string | null) {
    this.session.currentUserTestMode = value;
  }

  get currentExamTimeMinutes(): number | null {
    return this.session.currentExamTimeMinutes;
  }
  set currentExamTimeMinutes(value: number | null) {
    this.session.currentExamTimeMinutes = value;
  }

  get questionIdsLoadedForTest(): number | null {
    return this.session.questionIdsLoadedForTest;
  }
  set questionIdsLoadedForTest(value: number | null) {
    this.session.questionIdsLoadedForTest = value;
  }

  get subjects(): SubjectModel[] {
    return this.session.subjects;
  }
  set subjects(value: SubjectModel[]) {
    this.session.subjects = value;
  }

  constructor(
    private http: HttpClient,
    private runtimeConfig: RuntimeConfigService,
    private router: Router,
    private session: StudySessionStore,
    private progressSync: ProgressSyncService,
    private contentPacks: ContentPackService,
    private localDb: LocalDatabaseService,
    private connectivity: ConnectivityService,
    private localEntitlements: LocalEntitlementService,
    private deviceId: DeviceIdService,
  ) {
    this.initializeQueues();
  }

  /** Supabase project origin for Auth REST (browser POST may rely on Supabase CORS + CSP connect-src). */
  private supabaseAuthRequestBase(): string {
    const project = this.runtimeConfig.supabaseProjectUrl;
    if (!project) return '';
    return project.replace(/\/+$/, '');
  }

  /**
   * Supabase email/password (resource-owner). Requires `supabasePublishableKey` or `supabaseAnonKey` and project URL in runtime-config.
   * Returns the full token set so the caller can persist `refresh_token` for silent re-login.
   */
  async supabasePasswordGrant(email: string, password: string): Promise<SupabaseTokenSet | null> {
    const base = this.supabaseAuthRequestBase();
    const apiKey = this.runtimeConfig.supabaseApiKey;
    if (!base || !apiKey) {
      LoggerService.error(
        'Supabase password sign-in: missing project URL or publishable/anon API key in runtime-config',
        null,
      );
      return null;
    }
    const url = `${base}/auth/v1/token?grant_type=password`;
    const body = { email: email.trim(), password };
    return this.supabaseTokenRequest(url, body, apiKey, 'password');
  }

  /**
   * Exchange a stored Supabase refresh token for a fresh access JWT.
   * Returns `null` if Supabase rejects the token (logged-out, rotated, revoked).
   */
  async supabaseRefreshGrant(refreshToken: string): Promise<SupabaseTokenSet | null> {
    const base = this.supabaseAuthRequestBase();
    const apiKey = this.runtimeConfig.supabaseApiKey;
    if (!base || !apiKey || !refreshToken) {
      return null;
    }
    const url = `${base}/auth/v1/token?grant_type=refresh_token`;
    const body = { refresh_token: refreshToken };
    return this.supabaseTokenRequest(url, body, apiKey, 'refresh');
  }

  private async supabaseTokenRequest(
    url: string,
    body: Record<string, unknown>,
    apiKey: string,
    label: 'password' | 'refresh',
  ): Promise<SupabaseTokenSet | null> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      apikey: apiKey,
      Authorization: `Bearer ${apiKey}`,
    });
    try {
      const useNativeHttp = isCapacitorNative() || needsNativeDiscoveryFetch(url);
      const res = useNativeHttp
        ? await postJsonNative<{ access_token?: string; refresh_token?: string; expires_in?: number; expires_at?: number }>(
            url,
            body,
            { 'Content-Type': 'application/json', apikey: apiKey, Authorization: `Bearer ${apiKey}` },
          )
        : await firstValueFrom(
            this.http.post<{ access_token?: string; refresh_token?: string; expires_in?: number; expires_at?: number }>(
              url,
              body,
              { headers },
            ),
          );
      const accessToken = typeof res?.access_token === 'string' && res.access_token.length > 0 ? res.access_token : null;
      if (!accessToken) {
        LoggerService.error(`Supabase ${label} grant: missing access_token`, res);
        return null;
      }
      const refresh = typeof res?.refresh_token === 'string' && res.refresh_token.length > 0 ? res.refresh_token : null;
      let expiresAt: number | null = null;
      if (typeof res?.expires_at === 'number' && Number.isFinite(res.expires_at)) {
        expiresAt = res.expires_at * 1000;
      } else if (typeof res?.expires_in === 'number' && Number.isFinite(res.expires_in)) {
        expiresAt = Date.now() + res.expires_in * 1000;
      }
      return { accessToken, refreshToken: refresh, expiresAt };
    } catch (error) {
      LoggerService.error(`Supabase ${label} grant failed`, error);
      return null;
    }
  }

  async supabaseSignUp(
    email: string,
    password: string,
    redirectTo: string,
    policyAcceptance: PolicyAcceptance,
  ): Promise<void> {
    const base = this.supabaseAuthRequestBase();
    const apiKey = this.runtimeConfig.supabaseApiKey;
    if (!base || !apiKey) {
      throw new Error('Email sign-up is not configured.');
    }
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      apikey: apiKey,
      Authorization: `Bearer ${apiKey}`,
    });
    const url = `${base}/auth/v1/signup?redirect_to=${encodeURIComponent(redirectTo)}`;
    const response = await firstValueFrom(
      this.http.post<any>(url, {
        email: email.trim(),
        password,
        redirect_to: redirectTo,
        data: {
          terms_version: policyAcceptance.termsVersion,
          privacy_version: policyAcceptance.privacyVersion,
          terms_accepted_at: policyAcceptance.acceptedAt,
        },
      }, { headers })
    );
    const identities = response?.user?.identities ?? response?.identities;
    if (Array.isArray(identities) && identities.length === 0) {
      throw new Error('This email is already registered.');
    }
  }

  async getSupabasePolicyAcceptance(accessToken: string): Promise<Partial<PolicyAcceptance> | null> {
    const base = this.supabaseAuthRequestBase();
    const apiKey = this.runtimeConfig.supabaseApiKey;
    if (!base || !apiKey) return null;
    const headers = new HttpHeaders({ apikey: apiKey, Authorization: `Bearer ${accessToken}` });
    const response = await firstValueFrom(this.http.get<any>(`${base}/auth/v1/user`, { headers }));
    const metadata = response?.user_metadata ?? {};
    return {
      termsVersion: metadata.terms_version,
      privacyVersion: metadata.privacy_version,
      acceptedAt: metadata.terms_accepted_at,
    };
  }

  async updateSupabasePolicyAcceptance(accessToken: string, acceptance: PolicyAcceptance): Promise<void> {
    const base = this.supabaseAuthRequestBase();
    const apiKey = this.runtimeConfig.supabaseApiKey;
    if (!base || !apiKey) {
      throw new Error('Policy acceptance is not configured.');
    }
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      apikey: apiKey,
      Authorization: `Bearer ${accessToken}`,
    });
    await firstValueFrom(this.http.put(`${base}/auth/v1/user`, {
      data: {
        terms_version: acceptance.termsVersion,
        privacy_version: acceptance.privacyVersion,
        terms_accepted_at: acceptance.acceptedAt,
      },
    }, { headers }));
  }

  async getCurrentTermsStatus(sessionId: string): Promise<CurrentTermsStatus> {
    const headers = this.getAuthHeaders(sessionId);
    return await firstValueFrom(
      this.http.get<CurrentTermsStatus>(`${this.apiUrl}/privacy/terms`, { headers }),
    );
  }

  async acceptCurrentTerms(
    sessionId: string,
    policyVersion: string,
    source: string,
  ): Promise<CurrentTermsStatus> {
    const headers = this.getAuthHeaders(sessionId);
    return await firstValueFrom(
      this.http.post<CurrentTermsStatus>(
        `${this.apiUrl}/privacy/terms/acceptance`,
        { policyVersion, source },
        { headers },
      ),
    );
  }

  async createPrivacyExport(sessionId: string): Promise<PrivacyExportJob> {
    const headers = this.getAuthHeaders(sessionId);
    return await firstValueFrom(
      this.http.post<PrivacyExportJob>(`${this.apiUrl}/privacy/exports`, null, { headers }),
    );
  }

  async getPrivacyExportStatus(sessionId: string, jobId: string): Promise<PrivacyExportJob> {
    const headers = this.getAuthHeaders(sessionId);
    return await firstValueFrom(
      this.http.get<PrivacyExportJob>(
        `${this.apiUrl}/privacy/exports/${encodeURIComponent(jobId)}`,
        { headers },
      ),
    );
  }

  async downloadPrivacyExport(sessionId: string, jobId: string): Promise<Blob> {
    const headers = this.getAuthHeaders(sessionId);
    return await firstValueFrom(
      this.http.get(
        `${this.apiUrl}/privacy/exports/${encodeURIComponent(jobId)}/download`,
        { headers, responseType: 'blob' },
      ),
    );
  }

  async getOwnPrivacyRequests(sessionId: string): Promise<Record<string, unknown>[]> {
    const headers = this.getAuthHeaders(sessionId);
    return await firstValueFrom(
      this.http.get<Record<string, unknown>[]>(`${this.apiUrl}/privacy/requests`, { headers }),
    );
  }

  async getAdminPrivacyRequests(
    sessionId: string,
    status?: string,
  ): Promise<Record<string, unknown>[]> {
    const headers = this.getAuthHeaders(sessionId);
    const query = status ? `?status=${encodeURIComponent(status)}` : '';
    return await firstValueFrom(
      this.http.get<Record<string, unknown>[]>(`${this.apiUrl}/admin/privacy/requests${query}`, { headers }),
    );
  }

  async updateAdminPrivacyRequest(
    sessionId: string,
    requestId: number,
    update: { status?: string; adminNote?: string; resultReference?: string },
  ): Promise<Record<string, unknown>> {
    const headers = this.getAuthHeaders(sessionId);
    return await firstValueFrom(
      this.http.patch<Record<string, unknown>>(
        `${this.apiUrl}/admin/privacy/requests/${requestId}`,
        update,
        { headers },
      ),
    );
  }

  async getAdminDeletionResults(
    sessionId: string,
    limit = 100,
  ): Promise<Record<string, unknown>[]> {
    const headers = this.getAuthHeaders(sessionId);
    const safeLimit = Math.max(1, Math.min(limit, 500));
    return await firstValueFrom(
      this.http.get<Record<string, unknown>[]>(
        `${this.apiUrl}/admin/privacy/deletion-results?limit=${safeLimit}`,
        { headers },
      ),
    );
  }

  private initializeQueues() {
    LoggerService.log('Initializing background queues for question tracking');
    // Queue for marking questions as seen (debounced to batch rapid loads)
    this.seenQuestionsSub = this.seenQuestionsQueue$
      .pipe(
        debounceTime(500), // Batch multiple "seen" events within 500ms
        distinctUntilChanged((a, b) => a.userId === b.userId && a.questionId === b.questionId),
        concatMap(event => {
          LoggerService.log(`Processing seen question: ${event.questionId} for user ${event.userId}`);
          return this.markQuestionAsSeen(event.userId, event.questionId).pipe(
            catchError(err => {
              LoggerService.error('Failed to mark question as seen:', err);
              return of(null);
            })
          );
        })
      )
      .subscribe();

    // Queue for saving question answers (one at a time, preserving order)
    this.answerQueueSub = this.answerQueue$
      .pipe(
        concatMap(event => {
          if (event.questionId === 0 && event.userTestId === 0) {
            LoggerService.log('[answerQueue] Flush sentinel received, queue drained');
            if (this.answerQueueDrainResolve) {
              this.answerQueueDrainResolve();
              this.answerQueueDrainResolve = null;
            }
            return of(undefined);
          }
          console.log('[answerQueue] Processing answer save:', event);
          LoggerService.log(`Processing answer save: question ${event.questionId} in test ${event.userTestId}`);
          return this.saveQuestionAnswer(event).pipe(
            catchError(err => {
              console.error('[answerQueue] Failed to save answer:', err);
              LoggerService.error('Failed to save question answer:', err);
              return of(null);
            })
          );
        })
      )
      .subscribe();
    LoggerService.log('Background queues initialized');
  }

  accessDeniedMessage(denial: AccessDeniedResponse | null = this.lastAccessDenial): string {
    switch (denial?.reason) {
      case 'TRIAL_EXPIRED':
        return 'Your trial for this course has expired. Choose a plan to continue.';
      case 'DAILY_LIMIT_REACHED':
        return 'You reached today’s trial limit. Continue tomorrow or choose a plan.';
      case 'TOTAL_LIMIT_REACHED':
        return 'You reached the trial question limit. Choose a plan to continue.';
      case 'PROFILE_REQUIRED':
        return 'Please complete your profile before starting trial or course access.';
      case 'SUBSCRIPTION_REQUIRED':
      case 'NO_ACCESS_MAPPING':
        return 'This course requires an active subscription.';
      default:
        return 'Access denied for this course.';
    }
  }

  private captureAccessDenied(error: unknown): AccessDeniedResponse | null {
    const body = (error as any)?.error;
    if ((error as any)?.status === 403 && body && typeof body === 'object' && typeof body.reason === 'string') {
      this.lastAccessDenial = body as AccessDeniedResponse;
      LoggerService.log(`Access denied: ${this.accessDeniedMessage(this.lastAccessDenial)}`);
      if (this.lastAccessDenial.reason === 'PROFILE_REQUIRED') {
        void this.router.navigate(['/dashboard/my-profile'], {
          queryParams: { complete: '1', returnTo: this.router.url },
        });
      }
      return this.lastAccessDenial;
    }
    return null;
  }

  private markQuestionAsSeen(userId: number, questionId: number): Observable<void> {
    const sessionId = this.getSessionId();
    if (!sessionId) {
      LoggerService.log('No session, skipping mark as seen');
      return of(undefined);
    }
    
    const headers = this.getAuthHeaders(sessionId);
    return this.http.post(`${this.apiUrl}/user-questions`, 
      { userId, questionId }, 
      { headers }
    ).pipe(
      map(() => {
        LoggerService.log(`Marked question ${questionId} as seen for user ${userId}`);
        return undefined;
      }),
      catchError(error => {
        this.captureAccessDenied(error);
        LoggerService.error('Error marking question as seen:', error);
        throw error;
      })
    );
  }

  private saveQuestionAnswer(event: QuestionAnswerEvent): Observable<void> {
    console.log('[saveQuestionAnswer] Starting - userTestId:', event.userTestId, 'questionId:', event.questionId);
    if (!event.userTestId) {
      console.warn('[saveQuestionAnswer] No userTestId, skipping');
      LoggerService.log('No userTestId, skipping answer save');
      return of(undefined);
    }
    
    const sessionId = this.getSessionId();
    if (!sessionId) {
      console.warn('[saveQuestionAnswer] No session, skipping');
      LoggerService.log('No session, skipping answer save');
      return of(undefined);
    }
    
    console.log('[saveQuestionAnswer] Making HTTP POST to:', `${this.apiUrl}/user-tests/${event.userTestId}/questions`);
    const payload = {
      questionId: event.questionId,
      questionAnswer: event.selectedOption,
      correct: event.correct ? 1 : 0,
      answeredAt: event.answeredAt
    };
    console.log('[saveQuestionAnswer] Payload:', payload);
    LoggerService.log(`[saveQuestionAnswer] Saving answer: questionId=${event.questionId}, option=${event.selectedOption}, correct=${payload.correct}`);
    const headers = this.getAuthHeaders(sessionId);
    return this.http.post(`${this.apiUrl}/user-tests/${event.userTestId}/questions`, 
      payload, 
      { headers }
    ).pipe(
      map(() => {
        console.log('[saveQuestionAnswer] Success! Question:', event.questionId, 'Test:', event.userTestId, 'Correct:', payload.correct);
        LoggerService.log(`[saveQuestionAnswer] Successfully saved answer for question ${event.questionId} in test ${event.userTestId} - correct: ${payload.correct}`);
        return undefined;
      }),
      catchError(error => {
        console.error('[saveQuestionAnswer] HTTP Error:', error);
        LoggerService.error('Error saving question answer:', error);
        throw error;
      })
    );
  }

  public recordQuestionSeen(questionId: number) {
    const user = this.getCurrentUser();
    if (user?.id) {
      LoggerService.log(`Recording question ${questionId} as seen for user ${user.id}`);
      if (this.progressSync.shouldDeferToOutbox()) {
        void this.progressSync.enqueueQuestionSeen(user.id, questionId);
        return;
      }
      this.seenQuestionsQueue$.next({ userId: user.id, questionId });
    } else {
      LoggerService.log('No user ID, cannot record question as seen');
    }
  }

  /** Used by ProgressSyncService when flushing the outbox. */
  syncMarkQuestionSeen(userId: number, questionId: number): Observable<void> {
    return this.markQuestionAsSeen(userId, questionId);
  }

  /** Used by ProgressSyncService when flushing the outbox. */
  syncSaveQuestionAnswer(event: QuestionAnswerEvent): Observable<void> {
    return this.saveQuestionAnswer(event);
  }

  async syncSaveQuestionState(
    questionId: number,
    flagId: number | null,
    hide: number
  ): Promise<boolean> {
    const sessionId = this.getSessionId();
    if (!sessionId) {
      return false;
    }
    return this.saveQuestionState(sessionId, questionId, flagId, hide);
  }

  async syncSaveQuestionNotes(questionId: number, notes: string): Promise<boolean> {
    const sessionId = this.getSessionId();
    if (!sessionId) {
      return false;
    }
    return this.saveQuestionNotes(sessionId, questionId, notes);
  }

  async syncCreateUserTest(payload: import('../sync/sync-event.types').TestStartedPayload): Promise<number | null> {
    const sessionId = this.getSessionId();
    if (!sessionId) {
      return null;
    }
    const subjectIds = payload.subjectIds?.length
      ? payload.subjectIds
      : payload.subjectId != null
        ? [payload.subjectId]
        : [];
    return this.createUserTest(
      sessionId,
      payload.courseId,
      subjectIds,
      payload.mode,
      payload.questionIds,
      {
        amountQuestions: payload.amountQuestions ?? undefined,
        redFlagMode: payload.redFlagMode,
        yellowFlagMode: payload.yellowFlagMode,
        greenFlagMode: payload.greenFlagMode,
        excludeHidden: payload.excludeHidden,
        onlyUnseen: payload.onlyUnseen,
        onlyWrong: payload.onlyWrong,
        topicIds: payload.topicIds ?? undefined,
        seedId: payload.seedId ?? undefined,
      }
    );
  }

  async syncUpdateUserTest(
    userTestId: number,
    percentage: number,
    passed: boolean,
    timeEnd: string
  ): Promise<boolean> {
    const sessionId = this.getSessionId();
    if (!sessionId) {
      return false;
    }
    return this.updateUserTest(sessionId, userTestId, percentage, passed, timeEnd);
  }

  /** Batch push outbox events (POST /api/sync/push). Falls back to per-event REST if unavailable. */
  syncPush(events: { eventId: string; eventType: string; payload: unknown }[]): Observable<SyncPushResponse> {
    const sessionId = this.getSessionId();
    if (!sessionId) {
      return throwError(() => new Error('No session for sync push'));
    }
    const headers = this.getAuthHeaders(sessionId);
    const body = {
      events: events.map((e) => ({
        eventId: e.eventId,
        eventType: e.eventType,
        payload: e.payload,
      })),
    };
    return this.http.post<SyncPushResponse>(
      `${this.apiUrl}/sync/push`,
      body,
      { headers }
    );
  }

  /** Pull server-side changes since cursor (epoch seconds). */
  syncPull(since?: string): Observable<SyncPullResponse> {
    const sessionId = this.getSessionId();
    if (!sessionId) {
      return throwError(() => new Error('No session for sync pull'));
    }
    const headers = this.getAuthHeaders(sessionId);
    const params = since != null && since !== '' ? `?since=${encodeURIComponent(since)}` : '';
    return this.http.get<SyncPullResponse>(
      `${this.apiUrl}/sync/pull${params}`,
      { headers }
    );
  }

  public recordQuestionAnswer(event: QuestionAnswerEvent) {
    console.log('[recordQuestionAnswer] Event:', event);
    if (event.questionId === 0 && event.userTestId === 0) {
      this.answerQueue$.next(event);
      return;
    }
    if (this.progressSync.shouldDeferToOutbox()) {
      void this.progressSync.enqueueTestAnswer(event);
      return;
    }
    if (!event.userTestId) {
      console.error('[recordQuestionAnswer] WARNING: No userTestId! Question:', event.questionId, 'Current userTestId:', this.session.currentUserTestId);
      LoggerService.log(`WARNING: Attempting to record answer without userTestId for question ${event.questionId}. Current userTestId: ${this.session.currentUserTestId}`);
    } else {
      console.log('[recordQuestionAnswer] Adding to queue - userTestId:', event.userTestId, 'questionId:', event.questionId);
    }
    LoggerService.log(`Recording answer for question ${event.questionId} in test ${event.userTestId}`, event);
    this.answerQueue$.next(event);
  }

  /**
   * Wait for all pending answer saves to complete before navigating to results.
   * Call this before goToResults() so the report loads with up-to-date backend data.
   */
  public flushAnswerQueue(): Promise<void> {
    return new Promise(resolve => {
      this.answerQueueDrainResolve = resolve;
      this.answerQueue$.next({ ...RestService.FLUSH_SENTINEL });
    });
  }

  private getCurrentUser(): User | null {
    const userJson = localStorage.getItem(AuthService.userKey);
    if (!userJson) {
      return null;
    }
    try {
      return new User().fromJson(userJson);
    } catch (e) {
      LoggerService.error('Failed to parse user from localStorage', e);
      return null;
    }
  }

  private getSessionId(): string | null {
    const user = this.getCurrentUser();
    return user?.sid || null;
  }

  setExamTimeProgress(timeProgress: number) {
    this.session.timeProgressExam = timeProgress;
  }

  setMaxQuestions(newMax: number) {
    this.session.maxQuestions = newMax;
  }

  private getAuthHeaders(sessionId: string): HttpHeaders {
    return new HttpHeaders({
      'Authorization': `Bearer ${sessionId}`,
      'Content-Type': 'application/json',
      [this.deviceId.headerName()]: this.deviceId.getId()
    });
  }

  /** GET with bearer auth; uses native HTTP on Capacitor when the API host differs from the WebView origin. */
  private async authGetJson<T>(sessionId: string, path: string): Promise<T> {
    const url = `${this.apiUrl}${path.startsWith('/') ? path : `/${path}`}`;
    if (!this.connectivity.isOnline) {
      throw new Error(`Offline: skipped API request ${path}`);
    }
    const useNativeHttp = isCapacitorNative() || needsNativeDiscoveryFetch(url);
    if (useNativeHttp) {
      return getJsonNative<T>(url, {
        Authorization: `Bearer ${sessionId}`,
        'Content-Type': 'application/json',
        [this.deviceId.headerName()]: this.deviceId.getId(),
      });
    }
    const headers = this.getAuthHeaders(sessionId);
    return await firstValueFrom(this.http.get<T>(url, { headers }));
  }

  async login(username: string, password: string): Promise<LoginResponse | null> {
    try {
      const request: LoginRequest = { username, password };
      const loginUrl = `${this.apiUrl}/auth/login`;
      const useNativeHttp = isCapacitorNative() || needsNativeDiscoveryFetch(loginUrl);
      const response = useNativeHttp
        ? await postJsonNative<LoginResponse>(loginUrl, request)
        : await firstValueFrom(this.http.post<LoginResponse>(loginUrl, request));

      if (response && response.sessionId) {
        LoggerService.log('Login successful', { sessionId: response.sessionId, userId: response.userId });
        return response;
      }
      return null;
    } catch (error: any) {
      LoggerService.error('Login failed:', error);
      const msg = error?.error?.error ?? error?.error;
      if (msg) {
        LoggerService.error('Error message:', msg);
      }
      return null;
    }
  }

  private static readonly LOGOUT_HTTP_TIMEOUT_MS = 5_000;

  async logout(sessionId: string): Promise<boolean> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      await firstValueFrom(
        this.http.post(`${this.apiUrl}/auth/logout`, {}, { headers }).pipe(
          timeout(RestService.LOGOUT_HTTP_TIMEOUT_MS)
        )
      );
      LoggerService.log('Logout successful');
      return true;
    } catch (error) {
      LoggerService.error('Logout failed:', error);
      return false;
    }
  }

  async checkSession(sessionId: string): Promise<boolean> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      const response = await firstValueFrom(
        this.http.get<{ valid: boolean }>(`${this.apiUrl}/auth/session`, { headers })
      );
      return response?.valid || false;
    } catch (error) {
      LoggerService.error('Session check failed:', error);
      return false;
    }
  }

  /** Local profile for bearer token (OIDC access JWT or emergency session). */
  async fetchAuthMe(accessToken: string): Promise<LoginResponse | null> {
    try {
      const meUrl = `${this.apiUrl}/auth/me`;
      const useNativeHttp = isCapacitorNative() || needsNativeDiscoveryFetch(meUrl);
      if (useNativeHttp) {
        return await getJsonNative<LoginResponse>(meUrl, {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          [this.deviceId.headerName()]: this.deviceId.getId(),
        });
      }
      const headers = this.getAuthHeaders(accessToken);
      return await firstValueFrom(this.http.get<LoginResponse>(meUrl, { headers }));
    } catch (error) {
      LoggerService.error('Auth me failed:', error);
      return null;
    }
  }

  async getCourses(sessionId: string): Promise<Course[] | null> {
    if (!this.connectivity.isOnline) {
      const cached = await this.localEntitlements.getCourses();
      return cached ? this.mapCourses(cached) : null;
    }
    try {
      const response = await this.authGetJson<any[]>(sessionId, '/courses');
      
      LoggerService.log('Raw courses response from API:', response);
      
      if (!response || response.length === 0) {
        LoggerService.log('No courses returned from API');
        return [];
      }
      
      // The API returns CourseDTO with: courseId (String), courseTitle (String), courseCreationDate (String)
      const mappedCourses = this.mapCourses(response);
      
      LoggerService.log(`Successfully mapped ${mappedCourses.length} courses:`, mappedCourses);
      return mappedCourses;
    } catch (error: any) {
      LoggerService.error('Failed to fetch courses:', error);
      if (error.status === 401) {
        LoggerService.error('Authentication failed - check session token', null);
      }
      const cached = await this.localEntitlements.getCourses();
      return cached ? this.mapCourses(cached) : null;
    }
  }

  private mapCourses(response: Record<string, unknown>[]): Course[] {
    return response.map((course) => {
      const courseId = String(course['courseId'] ?? course['id'] ?? '');
      const courseTitle = String(course['courseTitle'] ?? course['name'] ?? '');
      const courseCreationDate = String(course['courseCreationDate'] ?? course['createdAt'] ?? '');
      LoggerService.log(`Mapping course - courseId: ${courseId}, courseTitle: ${courseTitle}`);
      return new Course(courseId, courseTitle, courseCreationDate);
    });
  }

  async getSubjects(sessionId: string, courseId: number): Promise<SubjectModel[] | null> {
    try {
      const response = await this.authGetJson<any[]>(sessionId, `/courses/${courseId}/subjects`);
      
      LoggerService.log(`Raw subjects response from API for course ${courseId}:`, response);
      
      if (!response || response.length === 0) {
        LoggerService.log(`No subjects returned for course ${courseId}`);
        return [];
      }
      
      // The API returns SubjectDTO with: subjectId (Integer), subjectTitle (String), examTimeMinutes (Integer), examQuestions (Integer)
      const mappedSubjects = response.map((subject: any) => {
        const subjectId = subject.subjectId || subject.id || 0;
        const subjectTitle = subject.subjectTitle || subject.name || '';
        const examTimeMinutes = subject.examTimeMinutes || null;
        const examQuestions = subject.examQuestions || null;
        
        LoggerService.log(`Mapping subject - subjectId: ${subjectId}, subjectTitle: ${subjectTitle}, examTimeMinutes: ${examTimeMinutes}, examQuestions: ${examQuestions}`);
        
        return new SubjectModel(0, subjectId, subjectTitle, examTimeMinutes, examQuestions);
      });
      
      LoggerService.log(`Successfully mapped ${mappedSubjects.length} subjects:`, mappedSubjects);
      return mappedSubjects;
    } catch (error: any) {
      this.captureAccessDenied(error);
      LoggerService.error('Failed to fetch subjects:', error);
      if (error.status === 401) {
        LoggerService.error('Authentication failed - check session token', null);
      }
      return null;
    }
  }

  async getTopicsForSubjects(sessionId: string, subjectIds: number[]): Promise<Topic[] | null> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      const response = await firstValueFrom(
        this.http.post<any[]>(`${this.apiUrl}/topics`, { subjectIds }, { headers })
      );

      if (!response || response.length === 0) {
        return [];
      }

      return response.map((t: any) => new Topic(
        t.id ?? 0,
        t.subjectId ?? t.subject_id ?? 0,
        t.name ?? ''
      ));
    } catch (error: any) {
      this.captureAccessDenied(error);
      LoggerService.error('Failed to fetch topics:', error);
      if (error.status === 401) {
        LoggerService.error('Authentication failed - check session token', null);
      }
      return null;
    }
  }

  async loadQuestionsMeta(
    sessionId: string,
    courseId: number,
    subjectIdsStr: string,
    filters?: {
      redFlagMode?: 'none' | 'dontUse' | 'constrainTo';
      yellowFlagMode?: 'none' | 'dontUse' | 'constrainTo';
      greenFlagMode?: 'none' | 'dontUse' | 'constrainTo';
      excludeHidden?: boolean;
      onlyUnseen?: boolean;
      onlyWrong?: boolean;
      topicIds?: number[];
    }
  ): Promise<number> {
    const subjectIds: number[] = this.convertToArrayOfNumbers(subjectIdsStr);
    
    console.log('[loadQuestionsMeta] START - courseId:', courseId, 'subjectIds:', subjectIds, 'filters:', filters);
    console.log('[loadQuestionsMeta] currentUserTestId before:', this.session.currentUserTestId);
    console.log('[loadQuestionsMeta] questionIds before:', this.session.questionIds);
    
    // Store current subjects for report
    this.session.currentSubjects = [];
    for (const subject of this.session.subjects) {
      if (subjectIds.includes(subject.subjectId)) {
        this.session.currentSubjects.push(subject.subjectTitle);
      }
    }

    const hasFilters = !!filters && Object.keys(filters).length > 0;
    if (isCapacitorNative() && this.contentPacks.isAutoDownloadEnabled() && !hasFilters) {
      if (!this.connectivity.isOnline) {
        const entitledCourses = await this.localEntitlements.getCourses();
        const entitled = entitledCourses?.some((course) =>
          Number(course['id'] ?? course['courseId']) === courseId
        );
        if (!entitled) {
          LoggerService.log('[loadQuestionsMeta] offline entitlement missing or expired', { courseId });
          return 0;
        }
      }
      const installed = await Promise.all(
        subjectIds.map(async (subjectId) => this.contentPacks.ensureSubjectPack(courseId, subjectId))
      );
      if (installed.every(Boolean)) {
        const localQuestionIds = await this.localDb.listLocalQuestionIds(courseId, subjectIds);
        if (localQuestionIds.length > 0) {
          this.session.questionIds = localQuestionIds;
          if (!this.session.currentUserTestId) {
            const settings = await this.ensureUserTestSettingsLoaded(sessionId);
            if (settings?.randomizeQuestions !== false) {
              this.session.questionIds = this.session.shuffleArray(this.session.questionIds);
            }
          }
          this.session.questionsMap = {};
          let prevId: number | null = null;
          this.session.questionIds.forEach((id, index) => {
            const nextId = index < this.session.questionIds.length - 1 ? this.session.questionIds[index + 1] : null;
            this.session.questionsMap[id] = new Question(id, "", "", nextId, prevId, true, "", []);
            prevId = id;
          });
          this.session.courseId = courseId;
          this.session.subjectIds = subjectIds;
          LoggerService.log('[loadQuestionsMeta] using local content pack', { courseId, subjectIds, count: localQuestionIds.length });
          return this.session.questionIds.length;
        }
      }
    }

    try {
      const headers = this.getAuthHeaders(sessionId);
      const request: any = {
        courseId: courseId,
        subjectIds: subjectIds
      };

      // Add filter parameters if provided
      if (filters) {
        request.filters = filters;
      }
      
      console.log('[loadQuestionsMeta] Making API call with request:', request);
      const response = await firstValueFrom(
        this.http.post<QuestionsMetaResponse>(`${this.apiUrl}/questions/meta`, request, { headers })
      );
      
      console.log('[loadQuestionsMeta] API response received:', response?.questionIds?.length || 0, 'question IDs');
      
      if (response && response.questionIds) {
        const receivedQuestionIds = response.questionIds;
        console.log('[loadQuestionsMeta] Received question IDs:', receivedQuestionIds.slice(0, 10), '... (showing first 10)');
        
        this.session.questionIds = receivedQuestionIds;
        // CRITICAL: Shuffle only when starting a new test (no currentUserTestId) AND user has randomizeQuestions enabled
        if (!this.session.currentUserTestId) {
          const settings = await this.ensureUserTestSettingsLoaded(sessionId);
          const randomize = settings?.randomizeQuestions !== false;
          if (randomize) {
            console.log('[loadQuestionsMeta] No currentUserTestId, shuffling question IDs (randomizeQuestions=true)');
            this.session.questionIds = this.session.shuffleArray(this.session.questionIds);
            console.log('[loadQuestionsMeta] Shuffled question IDs:', this.session.questionIds.slice(0, 10), '... (showing first 10)');
          } else {
            console.log('[loadQuestionsMeta] No currentUserTestId, NOT shuffling (randomizeQuestions=false)');
          }
        } else {
          console.log('[loadQuestionsMeta] currentUserTestId is set, NOT shuffling (preserving order for resume)');
        }
        
        this.session.questionsMap = {};
        let prevId: number | null = null;
        this.session.questionIds.forEach((id, index) => {
          const nextId = index < this.session.questionIds.length - 1 ? this.session.questionIds[index + 1] : null;
          this.session.questionsMap[id] = new Question(id, "", "", nextId, prevId, true, "", []);
          prevId = id;
        });
        
        console.log('[loadQuestionsMeta] Created questionsMap with', Object.keys(this.session.questionsMap).length, 'entries');
        
        this.session.courseId = courseId;
        this.session.subjectIds = subjectIds;
        
        return this.session.questionIds.length;
      }
      return 0;
    } catch (error) {
      this.captureAccessDenied(error);
      LoggerService.error('Failed to fetch questions meta:', error);
      console.error('[loadQuestionsMeta] Error:', error);
      return 0;
    }
  }

  async loadTopicCounts(sessionId: string, courseId: number, subjectIdsStr: string, filters?: any): Promise<Record<number, number>> {
    const subjectIds: number[] = this.convertToArrayOfNumbers(subjectIdsStr);
    try {
      const headers = this.getAuthHeaders(sessionId);
      const request: any = { courseId, subjectIds };
      if (filters && Object.keys(filters).length > 0) {
        request.filters = filters;
      }

      const response = await firstValueFrom(
        this.http.post<TopicCountResponse[]>(`${this.apiUrl}/questions/meta/topic-counts`, request, { headers })
      );

      const map: Record<number, number> = {};
      for (const row of (response || [])) {
        map[row.topicId] = Number(row.count) || 0;
      }
      return map;
    } catch (error: any) {
      this.captureAccessDenied(error);
      LoggerService.error('Failed to fetch topic counts:', error);
      return {};
    }
  }

  private async loadQuestions(sessionId: string, idWanted?: number) {
    if ((idWanted && this.session.questionsMap[idWanted]?.loaded) || this.session.isQuestionLoadLocked) {
      LoggerService.log("loadQ: loaded or locked" + Date.now());
      return;
    }

    while (this.session.isQuestionLoadLocked) {
      LoggerService.log("gQ looping: " + Date.now());
      await this.wait(100);
    }

    await this.session.acquireQuestionLoadLock();

    try {
      let questionsIdsStr = "";
      let loadAmount: number = 20;
      
      // Use limited question IDs (respects maxQuestions for current test)
      const limitedQuestionIds = this.getQuestionIds();

      if (limitedQuestionIds.length === 0) {
        LoggerService.log("No question IDs available to load");
        return;
      }

      // If a specific question is wanted, prioritize loading it first
      if (idWanted && (!this.session.questionsMap[idWanted] || !this.session.questionsMap[idWanted]?.loaded)) {
        questionsIdsStr = "" + idWanted;
        // Also load additional questions if we have space
        let j: number = 2; // Start at 2 since we already added idWanted
        let wantedIndex = limitedQuestionIds.indexOf(idWanted);
        if (wantedIndex >= 0) {
          // Load questions after the wanted one
          for (let i = wantedIndex + 1; i < limitedQuestionIds.length && j <= loadAmount; i++) {
            let id = limitedQuestionIds[i];
            if (!this.session.questionsMap[id]?.loaded) {
              questionsIdsStr += ',' + id;
              j++;
            }
          }
        }
      } else if (Object.keys(this.session.questionsMap).length > 0) {
        // Load unloaded questions in order
        let j: number = 1;
        for (let i = 0; i < limitedQuestionIds.length; i++) {
          let id = limitedQuestionIds[i];
          
          if (!this.session.questionsMap[id]?.loaded) {
            if (questionsIdsStr.length == 0) {
              questionsIdsStr = "" + id;
            } else {
              questionsIdsStr += ',' + id;
            }
            j++;
            if (j > loadAmount) {
              break;
            }
          }
        }
      } else {
        // Map is empty, load first batch of questions
        let j: number = 1;
        for (let i = 0; i < limitedQuestionIds.length && j <= loadAmount; i++) {
          let id = limitedQuestionIds[i];
          if (questionsIdsStr.length == 0) {
            questionsIdsStr = "" + id;
          } else {
            questionsIdsStr += ',' + id;
          }
          j++;
        }
      }

      if (!questionsIdsStr) {
        LoggerService.log("Nothing to load");
        return;
      }
      
      LoggerService.log("Loading " + questionsIdsStr);

      const questionIds = questionsIdsStr.split(',').map(id => parseInt(id));
      const request: QuestionsRequest = {
        courseId: this.session.courseId,
        subjectIds: this.session.subjectIds,
        questionIds: questionIds
      };

      if (isCapacitorNative() && this.contentPacks.isAutoDownloadEnabled()) {
        const localQuestions = await this.localDb.getLocalQuestions(
          this.session.courseId,
          this.session.subjectIds,
          questionIds
        );
        if (localQuestions.length === questionIds.length) {
          for (const q of localQuestions) {
            let prevId: number | null = null;
            let nextId: number | null = null;
            for (let j = 0; j < limitedQuestionIds.length; j++) {
              if (limitedQuestionIds[j] == q.id) {
                prevId = j > 0 ? limitedQuestionIds[j - 1] : null;
                nextId = j < limitedQuestionIds.length - 1 ? limitedQuestionIds[j + 1] : null;
                break;
              }
            }
            const answers = q.answers.map((ans, idx) => ({
              option: ans.option,
              label: ans.label,
              value: ans.isCorrect ? (idx + 1) : 0,
              isCorrect: ans.isCorrect,
              isChecked: false,
              isDisabled: false,
              tip: ans.tip || null
            }));
            this.session.questionsMap[q.id] = new Question(
              q.id,
              q.title,
              q.tip || null,
              nextId,
              prevId,
              true,
              q.image || null,
              answers
            );
            this.session.questionsMap[q.id]!.loaded = true;
          }
          LoggerService.log('[loadQuestions] loaded questions from local content pack', { count: localQuestions.length });
          return;
        }
      }

      const headers = this.getAuthHeaders(sessionId);
      const response = await firstValueFrom(
        this.http.post<Question[]>(`${this.apiUrl}/questions`, request, { headers })
      );
      
      for (let i = 0; i < response.length; i++) {
        let q = response[i];
        
        // Find prev/next in the limited test question order
        let prevId: number | null = null;
        let nextId: number | null = null;
        for (let j = 0; j < limitedQuestionIds.length; j++) {
          if (limitedQuestionIds[j] == q.id) {
            if (j > 0)
              prevId = limitedQuestionIds[j - 1];
            if (j < limitedQuestionIds.length - 1)
              nextId = limitedQuestionIds[j + 1];
            else
              nextId = null;
            break;
          }
        }

        // Map answers from DTO format
        const answers = q.answers.map((ans, idx) => ({
          option: ans.option,
          label: ans.label,
          value: ans.isCorrect ? (idx + 1) : 0,
          isCorrect: ans.isCorrect,
          isChecked: false,
          isDisabled: false,
          tip: ans.tip || null
        }));

        let imageUrl = q.image || null;
        const apiImageUrls = q.imageUrls?.length ? q.imageUrls : (imageUrl ? [imageUrl] : []);
        this.session.questionsMap[q.id] = new Question(
          q.id,
          q.title,
          q.tip || null,
          nextId,
          prevId,
          true,
          imageUrl,
          answers
        );
        this.session.questionsMap[q.id]!.loaded = true;
        this.assignResolvedQuestionImages(sessionId, q.id, apiImageUrls);
      }
    } catch (error) {
      LoggerService.error('Error loading questions:', error);
    } finally {
      this.session.releaseQuestionLoadLock();
      LoggerService.log("loadQ loading done: " + Date.now());
    }
  }

  convertToArrayOfNumbers(input: string): number[] {
    return input
      .split(',')
      .map(item => item.trim())
      .filter(item => item !== '')
      .map(Number);
  }

  public getQuestionIds(): number[] {
    return this.session.getQuestionIds();
  }

  public getQuestionsMap(): Id2Question {
    return this.session.getQuestionsMap();
  }

  public setQuestionIdsForResume(questionIds: number[]): void {
    this.session.setQuestionIdsForResume(questionIds);
  }

  async loadAndRestoreQuestionsForResume(sessionId: string, courseId: number, subjectIds: number[], questionIds: number[], answerMap: Map<number, { answer: string, correct: number }>): Promise<void> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      const request: QuestionsRequest = {
        courseId: courseId,
        subjectIds: subjectIds.length > 0 ? subjectIds : [],
        questionIds: questionIds
      };

      const response = await firstValueFrom(
        this.http.post<Question[]>(`${this.apiUrl}/questions`, request, { headers })
      );

      // Process each loaded question and restore state
      for (const q of response) {
        const answerInfo = answerMap.get(q.id);
        if (answerInfo && this.session.questionsMap[q.id]) {
          const question = this.session.questionsMap[q.id]!;
          question.answered = true;
          question.correctAnswer = answerInfo.correct === 1;
          question.loaded = true;
          question.title = q.title;
          question.tip = q.tip || null;
          question.image = q.image || null;
          const apiImageUrls = q.imageUrls?.length ? q.imageUrls : (q.image ? [q.image] : []);
          question.imageUrls = apiImageUrls.length ? [...apiImageUrls] : null;
          this.assignResolvedQuestionImages(sessionId, q.id, apiImageUrls);

          // Restore answer selection
          if (q.answers && q.answers.length > 0) {
            const answerIndex = answerInfo.answer.charCodeAt(0) - 65; // 'A'=0, 'B'=1, etc.
            const answers = q.answers.map((ans: any, idx: number) => ({
              option: ans.option,
              label: ans.label,
              value: ans.isCorrect ? (idx + 1) : 0,
              isCorrect: ans.isCorrect,
              isChecked: (idx === answerIndex),
              isDisabled: false,
              tip: ans.tip || null
            }));
            question.answers = answers;
          }
        }
      }
    } catch (error) {
      this.captureAccessDenied(error);
      LoggerService.error('Failed to load questions for resume:', error);
      throw error;
    }
  }

  public findFirstQuestion(): Question | null {
    return this.session.findFirstQuestion();
  }

  public reset(): void {
    this.session.reset();
  }

  public clearCurrentTestState(): void {
    this.session.clearCurrentTestState();
  }

  getRecreateSettings(): RecreateSettings | null {
    return this.session.getRecreateSettings();
  }

  setRecreateSettings(settings: RecreateSettings): void {
    this.session.setRecreateSettings(settings);
  }

  clearRecreateSettings(): void {
    this.session.clearRecreateSettings();
  }

  /** Build New Test prefill from a finished or saved test (same as Saved Tests → Recreate). */
  buildRecreateSettings(resumeData: UserTestResumeResponse): RecreateSettings {
    let topicIds: number[] | undefined;
    if (resumeData.topicIds) {
      try {
        const parsed = JSON.parse(resumeData.topicIds);
        if (Array.isArray(parsed)) {
          topicIds = parsed;
        }
      } catch {
        // ignore invalid JSON
      }
    }
    return {
      courseId: resumeData.courseId,
      subjectIds: resumeData.subjectIds ?? [],
      mode: resumeData.mode,
      amountQuestions: resumeData.amountQuestions,
      redFlagMode: resumeData.redFlagMode,
      yellowFlagMode: resumeData.yellowFlagMode,
      greenFlagMode: resumeData.greenFlagMode,
      excludeHidden: resumeData.excludeHidden === 1,
      onlyUnseen: resumeData.onlyUnseen === 1,
      onlyWrong: resumeData.onlyWrong === 1,
      topicIds,
      seedId: resumeData.seedId,
    };
  }

  buildTrainNextPrefill(recommendation: TrainNextRecommendation): RecreateSettings {
    return {
      courseId: recommendation.courseId,
      subjectIds: recommendation.subjectId ? [recommendation.subjectId] : [],
      mode: recommendation.mode === 'exam' ? 'exam' : 'study',
      amountQuestions: recommendation.questionIds?.length || recommendation.questionCount,
      redFlagMode: 'none',
      yellowFlagMode: 'none',
      greenFlagMode: 'none',
      excludeHidden: recommendation.filters?.excludeHidden ?? true,
      onlyUnseen: recommendation.filters?.onlyUnseen ?? false,
      onlyWrong: recommendation.filters?.onlyWrong ?? false,
      topicIds: recommendation.filters?.topicIds?.length
        ? [...recommendation.filters.topicIds]
        : undefined,
      questionIds: recommendation.questionIds?.length
        ? [...recommendation.questionIds]
        : undefined,
    };
  }

  public getFirstQuestionId(): number | null {
    return this.session.getFirstQuestionId();
  }

  private wait(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  public async getQuestion(id: number, sessionId: string): Promise<Question | null> {
    try {
      let question = this.session.questionsMap[id];
      if (!question || !question.loaded) {
        // Load the question - this will populate the map
        await this.loadQuestions(sessionId, id);
        // Get the question from the map after loading
        question = this.session.questionsMap[id];
      }
      
      // Verify the question is loaded before returning
      if (question && question.loaded) {
        // Don't record as seen here - will be recorded when answered
        return question;
      } else {
        LoggerService.log(`[getQuestion] Question ${id} not loaded after loadQuestions call`);
        return null;
      }
    } catch (error) {
      LoggerService.error(`[getQuestion] Error loading question ${id}:`, error);
      return null;
    }
  }

  public updateAnswers(questionFresh: Question, correct: boolean): void {
    this.session.updateAnswers(questionFresh, correct);
  }

  async getReport(): Promise<any> {
    console.log('[RestService.getReport] START');
    console.log('[RestService.getReport] currentUserTestId:', this.session.currentUserTestId);
    console.log('[RestService.getReport] questionIds in service:', this.session.questionIds);
    console.log('[RestService.getReport] questionsMap keys:', Object.keys(this.session.questionsMap));
    
    // Load answers from backend to ensure we have the correct saved state
    const sessionId = this.getSessionId();
    if (!sessionId || !this.session.currentUserTestId) {
      LoggerService.error(`[getReport] Cannot generate report: missing session (${!!sessionId}) or userTestId (${this.session.currentUserTestId})`, null);
      console.error('[RestService.getReport] Missing session or userTestId');
      return null;
    }

    LoggerService.log(`[getReport] Generating report for userTestId: ${this.session.currentUserTestId}`);
    console.log('[RestService.getReport] Calling getTestForResume for userTestId:', this.session.currentUserTestId);

    // Get test data from backend to ensure we have saved answers
    const resumeData = await this.getTestForResume(sessionId, this.session.currentUserTestId);
    if (!resumeData) {
      LoggerService.error(`[getReport] Cannot generate report: failed to load test data for userTestId: ${this.session.currentUserTestId}`, null);
      console.error('[RestService.getReport] Failed to load resume data');
      return null;
    }

    console.log('[RestService.getReport] Resume data loaded:', {
      userTestId: resumeData.userTestId,
      courseId: resumeData.courseId,
      subjectIds: resumeData.subjectIds,
      mode: resumeData.mode,
      questionCount: resumeData.questions.length
    });

    LoggerService.log(`[getReport] Loaded resume data with ${resumeData.questions.length} questions for userTestId: ${this.session.currentUserTestId}`);
    LoggerService.log(`[getReport] Resume data questions:`, resumeData.questions.map(q => ({
      questionId: q.questionId,
      questionAnswer: q.questionAnswer,
      correct: q.correct,
      answeredAt: q.answeredAt
    })));

    // Use question IDs from the backend resume data, not from current questionIds (which might be from a different test)
    const testQuestionIds = resumeData.questions.map(q => q.questionId);
    console.log('[RestService.getReport] Using question IDs from resume data:', testQuestionIds);
    LoggerService.log(`[getReport] Using ${testQuestionIds.length} question IDs from resume data:`, testQuestionIds);
    this.session.courseId = resumeData.courseId;
    this.session.subjectIds = resumeData.subjectIds ?? [];
    this.session.currentUserTestMode = resumeData.mode;
    this.session.setQuestionIdsForResume(testQuestionIds);
    
    // CRITICAL: Clear questionsMap entries that are NOT in this test to avoid mixing questions from different tests
    const questionsToKeep = new Set(testQuestionIds);
    const keysToRemove: number[] = [];
    for (const key of Object.keys(this.session.questionsMap)) {
      const id = +key;
      if (!questionsToKeep.has(id)) {
        keysToRemove.push(id);
      }
    }
    if (keysToRemove.length > 0) {
      console.log('[RestService.getReport] Removing', keysToRemove.length, 'questions from questionsMap that are not in this test:', keysToRemove);
      LoggerService.log(`[getReport] Removing ${keysToRemove.length} stale questions from questionsMap`);
      for (const id of keysToRemove) {
        delete this.session.questionsMap[id];
      }
    }
    
    // Load question data for all questions in this test if not already loaded
    const questionsToLoad: number[] = [];
    for (const id of testQuestionIds) {
      if (!this.session.questionsMap[id] || !this.session.questionsMap[id].loaded) {
        questionsToLoad.push(id);
      }
    }
    
    if (questionsToLoad.length > 0) {
      LoggerService.log(`[getReport] Loading ${questionsToLoad.length} questions that are not in questionsMap`);
      console.log('[RestService.getReport] Loading questions:', questionsToLoad);
      // Load questions in batches
      for (const id of questionsToLoad) {
        await this.getQuestion(id, sessionId);
      }
    } else {
      console.log('[RestService.getReport] All questions already loaded in questionsMap');
    }
    
    const filteredQuestionsMap: Id2Question = {};
    let answeredCount = 0;
    let unansweredCount = 0;
    let correctCount = 0;
    let wrongCount = 0;
    
    console.log('[RestService.getReport] Building filtered questions map...');
    // Build questions map with answers from backend
    for (const id of testQuestionIds) {
      if (this.session.questionsMap[id]) {
        // Get saved answer from backend
        const savedQuestion = resumeData.questions.find(q => q.questionId === id);
        const question = this.session.questionsMap[id];
        
        LoggerService.log(`[getReport] Processing question ${id}:`, {
          foundInBackend: !!savedQuestion,
          questionAnswer: savedQuestion?.questionAnswer,
          correct: savedQuestion?.correct,
          correctType: typeof savedQuestion?.correct,
          answeredAt: savedQuestion?.answeredAt
        });
        
        // Update question with saved answer state from backend
        if (savedQuestion && savedQuestion.questionAnswer != null) {
          // Question was answered
          question.answered = true;
          // Handle correct field - it can be 0, 1, or null/undefined
          const correctValue = savedQuestion.correct;
          question.correctAnswer = correctValue === 1;
          answeredCount++;
          if (question.correctAnswer) {
            correctCount++;
          } else {
            wrongCount++;
          }
          LoggerService.log(`[getReport] Question ${id} - answered: ${savedQuestion.questionAnswer}, correct value: ${correctValue} (${typeof correctValue}), correctAnswer: ${question.correctAnswer}`);
        } else {
          // Question was not answered
          question.answered = false;
          question.correctAnswer = false;
          unansweredCount++;
          if (savedQuestion) {
            LoggerService.log(`[getReport] Question ${id} - found in backend but questionAnswer is null`);
          } else {
            LoggerService.log(`[getReport] Question ${id} - not found in backend resume data`);
          }
        }
        
        filteredQuestionsMap[id] = question;
      } else {
        LoggerService.log(`[getReport] Question ${id} - not found in questionsMap after loading attempt`);
        console.warn(`[RestService.getReport] Question ${id} not found in questionsMap`);
      }
    }
    
    LoggerService.log(`[getReport] Summary: ${answeredCount} answered (${correctCount} correct, ${wrongCount} wrong), ${unansweredCount} unanswered`);
    console.log('[RestService.getReport] Summary:', { answeredCount, correctCount, wrongCount, unansweredCount });
    
    let questions = this.convertQuestionsMapToReportQuestions(filteredQuestionsMap);
    
    console.log('[RestService.getReport] Converted to report questions:', questions.length);
    // Log final question states
    LoggerService.log(`[getReport] Final questions in report:`, questions.map(q => ({
      id: q.id,
      answered: q.answered,
      correctAnswer: q.correctAnswer
    })));

    // Calculate time spent: timeProgressExam is a percentage (0-100), 
    // so we need to calculate actual time. For now, we'll use a placeholder.
    // TODO: Track actual exam start/end time to calculate real time spent
    const timeSpentMs = 0; // Placeholder - should be calculated from actual exam duration

    const report = {
      id: 1,
      testTitle: this.session.currentSubjects.join(' '),
      finishedAt: Date.now(),
      timeSpent: timeSpentMs,
      mode: this.session.currentUserTestMode || 'test', // Include mode, default to 'test'
      questions: questions
    };
    
    console.log('[RestService.getReport] Returning report with', report.questions.length, 'questions');
    return report;
  }

  convertQuestionsMapToReportQuestions(questionsMap: Id2Question) {
    return Object.entries(questionsMap).map(([id, question]) => {
      return question;
    }).filter(question => question !== null);
  }

  async searchQuestions(sessionId: string, searchParams: {
    courseId?: number | null;
    subjectId?: number | null;
    question?: string | null;
    answer?: string | null;
    explanation?: string | null;
    questionNumber?: string | null;
  }): Promise<QuestionSearchResponse[] | null> {
    this.lastAccessDenial = null;
    try {
      const headers = this.getAuthHeaders(sessionId);
      const response = await firstValueFrom(
        this.http.post<any[]>(`${this.apiUrl}/questions/search`, searchParams, { headers })
      );
      return response;
    } catch (error) {
      this.captureAccessDenied(error);
      LoggerService.error('Failed to search questions:', error);
      return null;
    }
  }

  async getAvailableQuestionFlags(sessionId: string): Promise<QuestionFlagResponse[] | null> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      const response = await firstValueFrom(
        this.http.get<QuestionFlagResponse[]>(`${this.apiUrl}/question-flags`, { headers })
      );
      return response;
    } catch (error) {
      LoggerService.error('Failed to load question flags:', error);
      return null;
    }
  }

  async searchFlaggedQuestions(sessionId: string, params: {
    flagIds?: number[];
    withNotes?: boolean;
  }): Promise<QuestionSearchResponse[] | null> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      const response = await firstValueFrom(
        this.http.post<QuestionSearchResponse[]>(`${this.apiUrl}/questions/flagged`, params, { headers })
      );
      return response;
    } catch (error) {
      LoggerService.error('Failed to search flagged questions:', error);
      return null;
    }
  }

  async getUserSubscriptions(sessionId: string): Promise<UserSubscriptionResponse[] | null> {
    if (!this.connectivity.isOnline) {
      return await this.localEntitlements.getSubscriptions() as UserSubscriptionResponse[] | null;
    }
    try {
      const headers = this.getAuthHeaders(sessionId);
      const response = await firstValueFrom(
        this.http.get<UserSubscriptionResponse[]>(`${this.apiUrl}/subscriptions`, { headers })
      );
      return response || [];
    } catch (error) {
      LoggerService.error('Failed to load user subscriptions:', error);
      return await this.localEntitlements.getSubscriptions() as UserSubscriptionResponse[] | null;
    }
  }

  async refreshOfflineEntitlement(sessionId: string, userId: number): Promise<boolean> {
    if (!this.connectivity.isOnline || !this.localDb.isNative) return false;
    try {
      const headers = this.getAuthHeaders(sessionId);
      const response = await firstValueFrom(
        this.http.get<{ token: string }>(`${this.apiUrl}/subscriptions/offline-entitlement`, { headers })
      );
      return !!response?.token && await this.localEntitlements.saveVerifiedToken(userId, response.token);
    } catch (error) {
      LoggerService.error('Failed to refresh offline entitlement:', error);
      return false;
    }
  }

  async getCoursePlanningOptions(sessionId: string): Promise<{ id: number; name: string; trialStatus?: string | null }[]> {
    const [courses, subscriptions] = await Promise.all([
      this.getUserCourses(sessionId),
      this.getUserSubscriptions(sessionId),
    ]);
    const byId = new Map<number, { id: number; name: string; trialStatus?: string | null }>();
    for (const course of courses ?? []) {
      const id = Number(course.id ?? course.courseId);
      if (Number.isFinite(id) && id > 0) {
        byId.set(id, { id, name: String(course.name ?? course.courseTitle ?? `Course #${id}`) });
      }
    }
    for (const subscription of subscriptions ?? []) {
      const id = Number(subscription.courseId);
      if (!Number.isFinite(id) || id <= 0 || byId.has(id)) {
        continue;
      }
      if (subscription.trialStatus === 'expired') {
        byId.set(id, {
          id,
          name: subscription.courseName || subscription.subscriptionName || `Course #${id}`,
          trialStatus: subscription.trialStatus,
        });
      }
    }
    return Array.from(byId.values());
  }

  async getUserTests(sessionId: string, mode?: string, subjectId?: number): Promise<UserTestListItem[] | null> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      let url = `${this.apiUrl}/user-tests`;
      const params: string[] = [];
      if (mode) {
        params.push(`mode=${mode}`);
      }
      if (subjectId) {
        params.push(`subjectId=${subjectId}`);
      }
      if (params.length > 0) {
        url += '?' + params.join('&');
      }
      
      const response = await firstValueFrom(
        this.http.get<UserTestListItem[]>(url, { headers })
      );
      return response;
    } catch (error) {
      LoggerService.error('Failed to fetch user tests:', error);
      return null;
    }
  }

  async getAvailableSubjects(sessionId: string): Promise<number[] | null> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      const response = await firstValueFrom(
        this.http.get<number[]>(`${this.apiUrl}/user-tests/subjects`, { headers })
      );
      return response;
    } catch (error) {
      LoggerService.error('Failed to fetch available subjects:', error);
      return null;
    }
  }

  /** Check if a username exists (for share dialog). Returns true if found, false if 404 or error. */
  async checkUsername(sessionId: string, username: string): Promise<boolean> {
    if (!username || !username.trim()) return false;
    try {
      const headers = this.getAuthHeaders(sessionId);
      const encoded = encodeURIComponent(username.trim());
      const response = await firstValueFrom(
        this.http.get<{ found: boolean }>(`${this.apiUrl}/user-tests/check-username?username=${encoded}`, { headers })
      );
      return response?.found === true;
    } catch (error: any) {
      if (error?.status === 404) return false;
      LoggerService.error('Failed to check username:', error);
      return false;
    }
  }

  /** Share a test with another user by username. Creates user_test_share record. Returns { ok, error? }. */
  async shareTest(sessionId: string, testId: number, username: string): Promise<{ ok: boolean; error?: string }> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      await firstValueFrom(
        this.http.post(`${this.apiUrl}/user-tests/${testId}/share`, { username: username.trim() }, { headers })
      );
      return { ok: true };
    } catch (error: unknown) {
      LoggerService.error('Failed to share test:', error);
      const err = error as { status?: number; error?: { error?: string } };
      const message = err?.status === 400 && typeof err?.error?.error === 'string'
        ? err.error.error
        : undefined;
      return { ok: false, error: message };
    }
  }

  /** Remove all shares for this test (current user must be owner). */
  async unshareTest(sessionId: string, testId: number): Promise<boolean> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      await firstValueFrom(
        this.http.delete(`${this.apiUrl}/user-tests/${testId}/share`, { headers })
      );
      return true;
    } catch (error) {
      LoggerService.error('Failed to unshare test:', error);
      return false;
    }
  }

  /** List tests shared WITH the current user (by others). */
  async getSharedTests(sessionId: string): Promise<UserTestListItem[] | null> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      const response = await firstValueFrom(
        this.http.get<UserTestListItem[]>(`${this.apiUrl}/user-tests/shared/list`, { headers })
      );
      return response;
    } catch (error) {
      LoggerService.error('Failed to fetch shared tests:', error);
      return null;
    }
  }

  /** Remove the share link for the current user (test no longer appears in Shared Tests). */
  async removeSharedWithMe(sessionId: string, testId: number): Promise<boolean> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      await firstValueFrom(
        this.http.delete(`${this.apiUrl}/user-tests/${testId}/share/me`, { headers })
      );
      return true;
    } catch (error) {
      LoggerService.error('Failed to remove shared test:', error);
      return false;
    }
  }

  async deleteUserTest(sessionId: string, userTestId: number): Promise<boolean> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      await firstValueFrom(
        this.http.delete(`${this.apiUrl}/user-tests/${userTestId}`, { headers })
      );
      return true;
    } catch (error) {
      LoggerService.error('Failed to delete user test:', error);
      return false;
    }
  }

  async hardDeleteUserTest(sessionId: string, userTestId: number): Promise<boolean> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      await firstValueFrom(
        this.http.delete(`${this.apiUrl}/user-tests/${userTestId}/hard`, { headers })
      );
      return true;
    } catch (error) {
      LoggerService.error('Failed to hard delete user test:', error);
      return false;
    }
  }

  async createUserTest(
    sessionId: string,
    courseId: number,
    subjectIds: number[],
    mode: string,
    questionIds?: number[],
    settings?: CreateUserTestSettings
  ): Promise<number | null> {
    const request: Record<string, unknown> = {
        courseId,
        subjectIds: subjectIds.length > 0 ? subjectIds : undefined,
        subjectId: subjectIds.length === 1 ? subjectIds[0] : undefined,
        mode,
        questionIds: questionIds ?? this.getQuestionIds(),
      };
    if (settings) {
      request['amountQuestions'] = settings.amountQuestions ?? null;
      request['redFlagMode'] = settings.redFlagMode ?? 'none';
      request['yellowFlagMode'] = settings.yellowFlagMode ?? 'none';
      request['greenFlagMode'] = settings.greenFlagMode ?? 'none';
      request['excludeHidden'] = settings.excludeHidden ?? true;
      request['onlyUnseen'] = settings.onlyUnseen ?? false;
      request['onlyWrong'] = settings.onlyWrong ?? false;
      request['topicIds'] = settings.topicIds ?? null;
      request['seedId'] = settings.seedId ?? null;
    }
    if (this.progressSync.shouldDeferToOutbox()) {
      const localUserTestId = -(Date.now() * 1000 + Math.floor(Math.random() * 1000));
      request['clientUserTestId'] = localUserTestId;
      await this.progressSync.enqueueTestStarted(
        request as unknown as import('../sync/sync-event.types').TestStartedPayload
      );
      LoggerService.log('[createUserTest] queued TEST_STARTED (offline)');
      this.session.currentUserTestMode = mode;
      this.session.courseId = courseId;
      localStorage.setItem(`${BRAND.storagePrefix}.${DASHBOARD_COURSE_STORAGE_SUFFIX}`, String(courseId));
      return localUserTestId;
    }
    try {
      const headers = this.getAuthHeaders(sessionId);
      const body: Record<string, unknown> = {
        ...request,
      };
      console.log('[createUserTest] Request body:', JSON.stringify(body));
      const response = await firstValueFrom(
        this.http.post<{ id: number }>(`${this.apiUrl}/user-tests`, body, { headers })
      );
      if (response.id) {
        this.session.currentUserTestMode = mode;
        this.session.courseId = courseId;
        localStorage.setItem(`${BRAND.storagePrefix}.${DASHBOARD_COURSE_STORAGE_SUFFIX}`, String(courseId));
      }
      return response.id;
    } catch (error) {
      this.captureAccessDenied(error);
      LoggerService.error('Failed to create user test:', error);
      return null;
    }
  }

  async updateUserTest(sessionId: string, userTestId: number, percentage: number, passed: boolean, timeEnd: string): Promise<boolean> {
    if (this.progressSync.shouldDeferToOutbox()) {
      await this.progressSync.enqueueTestFinished({
        userTestId,
        percentage,
        passed: passed ? 1 : 0,
        timeEnd,
      });
      return true;
    }
    try {
      const headers = this.getAuthHeaders(sessionId);
      const request = {
        percentage: percentage,
        passed: passed ? 1 : 0,
        timeEnd: timeEnd
      };
      await firstValueFrom(
        this.http.put(`${this.apiUrl}/user-tests/${userTestId}`, request, { headers })
      );
      return true;
    } catch (error) {
      LoggerService.error('Failed to update user test:', error);
      return false;
    }
  }

  async getTestForResume(sessionId: string, userTestId: number): Promise<UserTestResumeResponse | null> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      const response = await firstValueFrom(
        this.http.get<UserTestResumeResponse>(`${this.apiUrl}/user-tests/${userTestId}/resume`, { headers })
      );
      return response;
    } catch (error) {
      LoggerService.error('Failed to get test for resume:', error);
      return null;
    }
  }

  async getDashboardStatistics(sessionId: string): Promise<DashboardStatistics | null> {
    const cacheKey = 'dashboard-stats';
    try {
      const headers = this.getAuthHeaders(sessionId);
      const response = await firstValueFrom(
        this.http.get<DashboardStatistics>(`${this.apiUrl}/dashboard/statistics`, { headers })
      );
      this.saveRetentionCache(cacheKey, response);
      return response;
    } catch (error) {
      LoggerService.error('Failed to get dashboard statistics:', error);
      return this.loadRetentionCache<DashboardStatistics>(cacheKey);
    }
  }

  async getTrainNextRecommendation(sessionId: string, courseId: number): Promise<TrainNextRecommendation | null> {
    const cacheKey = `train-next:${courseId}`;
    try {
      const headers = this.getAuthHeaders(sessionId);
      const response = await firstValueFrom(
        this.http.get<TrainNextRecommendation>(`${this.apiUrl}/retention/train-next?courseId=${courseId}`, { headers })
      );
      this.saveRetentionCache(cacheKey, response);
      return response;
    } catch (error) {
      LoggerService.error('Failed to load Train next recommendation:', error);
      const cached = await this.loadRetentionCache<TrainNextRecommendation>(cacheKey);
      if (cached) {
        return cached;
      }
      return this.getOfflineTrainNextRecommendation(courseId);
    }
  }

  async acceptTrainNextRecommendation(sessionId: string, recommendation: TrainNextRecommendation): Promise<boolean> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      await firstValueFrom(
        this.http.post(`${this.apiUrl}/retention/train-next/accepted`, {
          courseId: recommendation.courseId,
          subjectId: recommendation.subjectId ?? null,
          recommendationType: recommendation.recommendationType,
          mode: recommendation.mode,
          questionCount: recommendation.questionCount,
          ctaLabel: recommendation.ctaLabel,
        }, { headers })
      );
      return true;
    } catch (error) {
      LoggerService.error('Failed to accept Train next recommendation:', error);
      return false;
    }
  }

  async getResultFeedback(sessionId: string, userTestId: number): Promise<ResultFeedback | null> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      return await firstValueFrom(
        this.http.get<ResultFeedback>(`${this.apiUrl}/retention/result-feedback?userTestId=${userTestId}`, { headers })
      );
    } catch (error) {
      LoggerService.error('Failed to load result feedback:', error);
      return null;
    }
  }

  async saveCourseGoal(sessionId: string, request: SaveCourseGoalRequest): Promise<CourseGoal | null> {
    if (this.progressSync.shouldDeferToOutbox()) {
      const dailyQuestionTarget = await this.calculateOfflineDailyTarget(request);
      const preferredSessionSize = this.deriveSessionSize(dailyQuestionTarget);
      const goal: CourseGoal = {
        courseId: request.courseId,
        targetDate: request.targetDate,
        weeklyTrainingDays: 7,
        includeWeekends: 1,
        preferredSessionSize,
        maxDailySuggestedQuestions: 500,
        dailyQuestionTarget,
      };
      await this.localDb.upsertLocalCourseGoal({ ...goal, updatedAt: new Date().toISOString() });
      await this.progressSync.enqueueCourseGoalSet({
        courseId: request.courseId,
        targetDate: request.targetDate,
        leaderboardOptIn: request.leaderboardOptIn,
      });
      return goal;
    }
    try {
      const headers = this.getAuthHeaders(sessionId);
      const response = await firstValueFrom(
        this.http.put<CourseGoal>(`${this.apiUrl}/retention/course-goal`, request, { headers })
      );
      if (isCapacitorNative()) {
        await this.localDb.upsertLocalCourseGoal({ ...response, updatedAt: new Date().toISOString() });
      }
      return response;
    } catch (error) {
      LoggerService.error('Failed to save course goal:', error);
      return null;
    }
  }

  async getCourseGoalSettings(sessionId: string, courseId: number): Promise<CourseGoalSettings | null> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      const response = await firstValueFrom(
        this.http.get<CourseGoalSettings>(`${this.apiUrl}/retention/course-goal?courseId=${courseId}`, { headers })
      );
      return response;
    } catch (error) {
      LoggerService.error('Failed to load course goal settings:', error);
      const goal = await this.localDb.getLocalCourseGoal(courseId).catch(() => null);
      const stats = await this.localDb.getOfflineTrainNextStats(courseId).catch(() => null);
      if (!stats) return null;
      return {
        goal,
        goalSetup: this.buildOfflineGoalSetup(stats.totalQuestions, stats.questionsSeen),
      };
    }
  }

  async saveNotificationPreferences(
    sessionId: string,
    request: SaveNotificationPreferenceRequest
  ): Promise<NotificationPreference | null> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      return await firstValueFrom(
        this.http.put<NotificationPreference>(`${this.apiUrl}/retention/notification-preferences`, request, { headers })
      );
    } catch (error) {
      LoggerService.error('Failed to save notification preferences:', error);
      return null;
    }
  }

  async getOfflineDefaultCourseId(): Promise<number | null> {
    if (!isCapacitorNative()) return null;
    const ids = await this.localDb.listInstalledCourseIds().catch(() => []);
    return ids[0] ?? null;
  }

  async syncSaveCourseGoal(request: SaveCourseGoalRequest): Promise<CourseGoal | null> {
    const sessionId = this.getSessionId();
    if (!sessionId) return null;
    return this.saveCourseGoal(sessionId, request);
  }

  async getRetentionFeatureFlags(sessionId: string): Promise<RetentionFeatureFlags | null> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      return await firstValueFrom(
        this.http.get<RetentionFeatureFlags>(`${this.apiUrl}/retention/feature-flags`, { headers })
      );
    } catch (error) {
      LoggerService.error('Failed to get retention feature flags:', error);
      return null;
    }
  }

  async getCoachReminder(sessionId: string, courseId: number): Promise<CoachReminder | null> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      return await firstValueFrom(
        this.http.get<CoachReminder>(`${this.apiUrl}/retention/coach-reminder?courseId=${courseId}`, { headers })
      );
    } catch (error: any) {
      if (error?.status !== 204) {
        LoggerService.error('Failed to get coach reminder:', error);
      }
      return null;
    }
  }

  async getSocialSummary(sessionId: string): Promise<SocialSummary | null> {
    const cacheKey = 'social-summary';
    try {
      const headers = this.getAuthHeaders(sessionId);
      const response = await firstValueFrom(
        this.http.get<SocialSummary>(`${this.apiUrl}/retention/social-summary`, { headers })
      );
      this.saveRetentionCache(cacheKey, response);
      return response;
    } catch (error) {
      LoggerService.error('Failed to get social summary:', error);
      return this.loadRetentionCache<SocialSummary>(cacheKey);
    }
  }

  async getFriends(sessionId: string, courseId?: number): Promise<FriendSummary[]> {
    const cacheKey = courseId != null ? `friends:${courseId}` : 'friends:all';
    try {
      const headers = this.getAuthHeaders(sessionId);
      const query = courseId != null ? `?courseId=${courseId}` : '';
      const response = await firstValueFrom(
        this.http.get<FriendSummary[]>(`${this.apiUrl}/retention/friends${query}`, { headers })
      );
      this.saveRetentionCache(cacheKey, response);
      return response;
    } catch (error) {
      LoggerService.error('Failed to get friends:', error);
      return (await this.loadRetentionCache<FriendSummary[]>(cacheKey)) ?? [];
    }
  }

  async getFriendRequests(sessionId: string): Promise<FriendRequestSummary[]> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      return await firstValueFrom(
        this.http.get<FriendRequestSummary[]>(`${this.apiUrl}/retention/friend-requests`, { headers })
      );
    } catch (error) {
      LoggerService.error('Failed to get friend requests:', error);
      return [];
    }
  }

  async getFriendSuggestions(sessionId: string): Promise<FriendSuggestion[]> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      return await firstValueFrom(
        this.http.get<FriendSuggestion[]>(`${this.apiUrl}/retention/friend-suggestions`, { headers })
      );
    } catch (error) {
      LoggerService.error('Failed to get friend suggestions:', error);
      return [];
    }
  }

  async checkFriendUsername(sessionId: string, username: string): Promise<boolean> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      const response = await firstValueFrom(
        this.http.get<{ found: boolean }>(
          `${this.apiUrl}/retention/friend-lookup?username=${encodeURIComponent(username)}`,
          { headers }
        )
      );
      return response?.found === true;
    } catch (error) {
      LoggerService.error('Failed to look up friend username:', error);
      return false;
    }
  }

  async sendFriendRequest(sessionId: string, username: string): Promise<{ ok: boolean; error?: string }> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      await firstValueFrom(
        this.http.post(`${this.apiUrl}/retention/friend-requests`, { username }, { headers })
      );
      return { ok: true };
    } catch (error: any) {
      const msg = error?.error?.error || 'Failed to send friend request';
      return { ok: false, error: msg };
    }
  }

  async acceptFriendRequest(sessionId: string, friendshipId: number): Promise<boolean> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      await firstValueFrom(
        this.http.post(`${this.apiUrl}/retention/friend-requests/${friendshipId}/accept`, {}, { headers })
      );
      return true;
    } catch (error) {
      LoggerService.error('Failed to accept friend request:', error);
      return false;
    }
  }

  async declineFriendRequest(sessionId: string, friendshipId: number): Promise<boolean> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      await firstValueFrom(
        this.http.post(`${this.apiUrl}/retention/friend-requests/${friendshipId}/decline`, {}, { headers })
      );
      return true;
    } catch (error) {
      LoggerService.error('Failed to decline friend request:', error);
      return false;
    }
  }

  async cancelFriendRequest(sessionId: string, friendshipId: number): Promise<boolean> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      await firstValueFrom(
        this.http.post(`${this.apiUrl}/retention/friend-requests/${friendshipId}/cancel`, {}, { headers })
      );
      return true;
    } catch (error) {
      LoggerService.error('Failed to cancel friend request:', error);
      return false;
    }
  }

  async getLeaderboard(sessionId: string, courseId: number): Promise<LeaderboardResponse | null> {
    const cacheKey = `leaderboard:${courseId}`;
    try {
      const headers = this.getAuthHeaders(sessionId);
      const response = await firstValueFrom(
        this.http.get<LeaderboardResponse>(`${this.apiUrl}/retention/leaderboard?courseId=${courseId}`, { headers })
      );
      this.saveRetentionCache(cacheKey, response);
      return response;
    } catch (error) {
      LoggerService.error('Failed to get leaderboard:', error);
      return this.loadRetentionCache<LeaderboardResponse>(cacheKey);
    }
  }

  async getUserProfile(sessionId: string): Promise<any | null> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      const response = await firstValueFrom(
        this.http.get<any>(`${this.apiUrl}/user/profile`, { headers })
      );
      return response;
    } catch (error) {
      LoggerService.error('Failed to get user profile:', error);
      return null;
    }
  }

  async updateUserProfile(sessionId: string, profileData: {
    email?: string;
    phoneCountryPrefix?: string;
    phoneNumber?: string;
    acceptMarketing?: boolean;
  }): Promise<boolean> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      await firstValueFrom(
        this.http.put(`${this.apiUrl}/user/profile`, profileData, { headers })
      );
      return true;
    } catch (error) {
      LoggerService.error('Failed to update user profile:', error);
      return false;
    }
  }

  async changePassword(sessionId: string, passwordData: {
    currentPassword: string;
    newPassword: string;
  }): Promise<boolean> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      await firstValueFrom(
        this.http.put(`${this.apiUrl}/user/password`, passwordData, { headers })
      );
      return true;
    } catch (error: any) {
      LoggerService.error('Failed to change password:', error);
      // Re-throw the error so the component can handle it and show inline error
      throw error;
    }
  }

  async changeSupabasePassword(email: string, currentPassword: string, newPassword: string): Promise<boolean> {
    const base = this.supabaseAuthRequestBase();
    const apiKey = this.runtimeConfig.supabaseApiKey;
    if (!base || !apiKey) {
      throw new Error('Password change is not configured.');
    }
    const authHeaders = new HttpHeaders({
      'Content-Type': 'application/json',
      apikey: apiKey,
      Authorization: `Bearer ${apiKey}`,
    });
    try {
      const signIn = await firstValueFrom(
        this.http.post<{ access_token?: string }>(
          `${base}/auth/v1/token?grant_type=password`,
          { email: email.trim(), password: currentPassword },
          { headers: authHeaders },
        )
      );
      const accessToken = signIn?.access_token;
      if (!accessToken) {
        const err = new Error('Current password is incorrect. Please try again.') as Error & { code?: string };
        err.code = 'CURRENT_PASSWORD_INVALID';
        throw err;
      }
      const updateHeaders = new HttpHeaders({
        'Content-Type': 'application/json',
        apikey: apiKey,
        Authorization: `Bearer ${accessToken}`,
      });
      await firstValueFrom(
        this.http.put(`${base}/auth/v1/user`, { password: newPassword }, { headers: updateHeaders })
      );
      return true;
    } catch (error: any) {
      if (error?.code === 'CURRENT_PASSWORD_INVALID') {
        throw error;
      }
      const body = error?.error;
      const message =
        (typeof body === 'string' ? body : null) ||
        body?.error_description ||
        body?.msg ||
        body?.message ||
        body?.error ||
        error?.message ||
        'Password change failed.';
      if (error?.status === 401 || error?.status === 403) {
        const err = new Error('Current password is incorrect. Please try again.') as Error & { code?: string };
        err.code = 'CURRENT_PASSWORD_INVALID';
        throw err;
      }
      if (error?.status === 405) {
        throw new Error('Password change is currently unavailable. Please try again later.');
      }
      throw new Error(message);
    }
  }

  async deleteAccount(sessionId: string): Promise<{ status: string; retentionDays: number; deletionScheduledAt: string } | null> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      return await firstValueFrom(
        this.http.delete<{ status: string; retentionDays: number; deletionScheduledAt: string }>(`${this.apiUrl}/user/account`, { headers })
      );
    } catch (error) {
      LoggerService.error('Failed to delete account:', error);
      return null;
    }
  }

  async cancelAccountDeletion(sessionId: string): Promise<boolean> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      await firstValueFrom(
        this.http.post(`${this.apiUrl}/user/account/cancel-deletion`, {}, { headers })
      );
      return true;
    } catch (error) {
      LoggerService.error('Failed to cancel account deletion:', error);
      return false;
    }
  }

  /** Contact form: config including maxDailyUserMessages, messagesSentToday, unreadCount. */
  async getContactConfig(sessionId: string): Promise<{ contactEnabled: boolean; maxDailyUserMessages?: number; messagesSentToday?: number; unreadCount?: number } | null> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      const response = await firstValueFrom(
        this.http.get<{ contactEnabled: boolean; maxDailyUserMessages?: number; messagesSentToday?: number; unreadCount?: number }>(`${this.apiUrl}/contact/config`, { headers })
      );
      return response;
    } catch (error) {
      LoggerService.error('Failed to get contact config:', error);
      return null;
    }
  }

  /** User: list contact messages (inbox/sent) */
  async getContactMessages(sessionId: string, page = 0, size = 100, sort = 'createdAt,desc', inbox: boolean): Promise<any> {
    const headers = this.getAuthHeaders(sessionId);
    const url = `${this.apiUrl}/contact/messages?page=${page}&size=${size}&sort=${encodeURIComponent(sort)}&inbox=${inbox}`;
    return await firstValueFrom(this.http.get<any>(url, { headers }));
  }

  /** User: get message thread */
  async getContactMessageThread(sessionId: string, id: number): Promise<any[]> {
    const headers = this.getAuthHeaders(sessionId);
    return await firstValueFrom(this.http.get<any[]>(`${this.apiUrl}/contact/messages/${id}/thread`, { headers }));
  }

  /** User: reply to message (multipart) - do NOT set Content-Type; browser sets it with boundary */
  async replyContactMessage(sessionId: string, id: number, replyText: string, attachment?: File): Promise<any> {
    const headers = new HttpHeaders({ 'Authorization': `Bearer ${sessionId}` });
    const formData = new FormData();
    formData.append('replyText', replyText);
    if (attachment) formData.append('attachment', attachment);
    return await firstValueFrom(this.http.post<any>(`${this.apiUrl}/contact/messages/${id}/reply`, formData, { headers }));
  }

  /** User: download message attachment */
  async getContactMessageAttachment(sessionId: string, id: number): Promise<Blob> {
    const headers = this.getAuthHeaders(sessionId);
    return await firstValueFrom(this.http.get(`${this.apiUrl}/contact/messages/${id}/attachment`, { headers, responseType: 'blob' }));
  }

  /** Submit contact form. Returns { id } on success, or throws with error message on 400. */
  async submitContact(sessionId: string, data: {
    topic: string;
    messageText: string;
  }): Promise<{ id: number }> {
    const headers = this.getAuthHeaders(sessionId);
    const response = await firstValueFrom(
      this.http.post<{ id: number }>(`${this.apiUrl}/contact`, data, { headers })
    );
    return response;
  }

  async putAdminTrialPolicy(sessionId: string, subscriptionId: number, body: any): Promise<any> {
    const headers = this.getAuthHeaders(sessionId);
    return await firstValueFrom(this.http.put<any>(`${this.apiUrl}/admin/subscriptions/${subscriptionId}/trial-policy`, body, { headers }));
  }

  async getCountries(sessionId: string): Promise<any[] | null> {
    try {
      return await this.authGetJson<any[]>(sessionId, '/user/countries');
    } catch (error) {
      LoggerService.error('Failed to get countries:', error);
      return null;
    }
  }

  async getAuthorities(sessionId: string, countryId?: number): Promise<any[] | null> {
    try {
      const path = countryId
        ? `/user/authorities?countryId=${countryId}`
        : '/user/authorities';
      return await this.authGetJson<any[]>(sessionId, path);
    } catch (error) {
      LoggerService.error('Failed to get authorities:', error);
      return null;
    }
  }

  async getUserTestSettings(sessionId: string): Promise<any | null> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      const response = await firstValueFrom(
        this.http.get<any>(`${this.apiUrl}/settings/test-settings`, { headers })
      );
      return response;
    } catch (error) {
      LoggerService.error('Failed to get user test settings:', error);
      return null;
    }
  }

  /** Load and cache user test settings. Returns cached settings for use during tests. */
  async ensureUserTestSettingsLoaded(sessionId: string): Promise<{ shuffleQuestionAnswers: boolean; randomizeQuestions: boolean; nextAutomatically: boolean } | null> {
    const cached = this.session.getUserTestSettingsCache();
    if (cached != null) {
      return cached;
    }
    const settings = await this.getUserTestSettings(sessionId);
    if (settings) {
      const value = {
        shuffleQuestionAnswers: !!settings.shuffleQuestionAnswers,
        randomizeQuestions: settings.randomizeQuestions !== false && settings.randomizeQuestions !== 0,
        nextAutomatically: !!settings.nextAutomatically,
      };
      this.session.setUserTestSettingsCache(value);
      return value;
    }
    const defaults = {
      shuffleQuestionAnswers: false,
      randomizeQuestions: true,
      nextAutomatically: false,
    };
    this.session.setUserTestSettingsCache(defaults);
    return defaults;
  }

  getCachedUserTestSettings(): { shuffleQuestionAnswers: boolean; randomizeQuestions: boolean; nextAutomatically: boolean } | null {
    return this.session.getUserTestSettingsCache();
  }

  /** Clear the user test settings cache so next load fetches fresh values (e.g. after user saves new settings). */
  clearUserTestSettingsCache(): void {
    this.session.clearUserTestSettingsCache();
  }

  async updateUserTestSettings(sessionId: string, settings: any): Promise<boolean> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      await firstValueFrom(
        this.http.put(`${this.apiUrl}/settings/test-settings`, settings, { headers })
      );
      return true;
    } catch (error) {
      LoggerService.error('Failed to update user test settings:', error);
      return false;
    }
  }

  async getQuestionSeeds(sessionId: string): Promise<any[] | null> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      const response = await firstValueFrom(
        this.http.get<any[]>(`${this.apiUrl}/settings/question-seeds`, { headers })
      );
      return response;
    } catch (error) {
      LoggerService.error('Failed to get question seeds:', error);
      return null;
    }
  }

  async getUserCourses(sessionId: string): Promise<any[] | null> {
    if (!this.connectivity.isOnline) {
      return await this.localEntitlements.getCourses();
    }
    try {
      return await this.authGetJson<any[]>(sessionId, '/settings/courses');
    } catch (error) {
      LoggerService.error('Failed to get user courses:', error);
      return await this.localEntitlements.getCourses();
    }
  }

  async getSubjectsForCourse(sessionId: string, courseId: number): Promise<any[] | null> {
    try {
      return await this.authGetJson<any[]>(sessionId, `/settings/courses/${courseId}/subjects`);
    } catch (error) {
      LoggerService.error('Failed to get subjects for course:', error);
      return null;
    }
  }

  async resetSubject(sessionId: string, subjectId: number): Promise<boolean> {
    if (this.progressSync.shouldDeferToOutbox()) {
      const resetAt = new Date().toISOString();
      await this.localDb.upsertLocalSubjectReset(subjectId, resetAt);
      await this.progressSync.enqueueSubjectReset({ subjectId, resetAt });
      return true;
    }
    try {
      const headers = this.getAuthHeaders(sessionId);
      await firstValueFrom(
        this.http.post(`${this.apiUrl}/settings/reset-subject`, { subjectId }, { headers })
      );
      if (isCapacitorNative()) {
        await this.localDb.upsertLocalSubjectReset(subjectId, new Date().toISOString());
      }
      return true;
    } catch (error) {
      LoggerService.error('Failed to reset subject:', error);
      return false;
    }
  }

  async syncResetSubject(subjectId: number): Promise<boolean> {
    const sessionId = this.getSessionId();
    if (!sessionId) return false;
    return this.resetSubject(sessionId, subjectId);
  }

  private retentionStorageKey(cacheKey: string): string {
    return `${BRAND.storagePrefix}.retention.${cacheKey}`;
  }

  private saveRetentionCache(cacheKey: string, data: unknown): void {
    const json = JSON.stringify(data);
    try {
      sessionStorage.setItem(this.retentionStorageKey(cacheKey), json);
    } catch {
      // ignore quota errors
    }
    if (isCapacitorNative()) {
      void this.localDb.upsertRetentionCache(cacheKey, json);
    }
  }

  private async loadRetentionCache<T>(cacheKey: string): Promise<T | null> {
    if (isCapacitorNative()) {
      const raw = await this.localDb.getRetentionCache(cacheKey).catch(() => null);
      if (raw) {
        try {
          return JSON.parse(raw) as T;
        } catch {
          // fall through
        }
      }
    }
    try {
      const raw = sessionStorage.getItem(this.retentionStorageKey(cacheKey));
      if (raw) {
        return JSON.parse(raw) as T;
      }
    } catch {
      // ignore parse errors
    }
    return null;
  }

  private async calculateOfflineDailyTarget(request: SaveCourseGoalRequest): Promise<number> {
    const stats = await this.localDb.getOfflineTrainNextStats(request.courseId).catch(() => null);
    const remaining = stats ? Math.max(0, stats.totalQuestions - stats.questionsSeen) : 20;
    const target = new Date(`${request.targetDate}T00:00:00`);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const days = Math.max(1, Math.ceil((target.getTime() - today.getTime()) / 86_400_000) + 1);
    return Math.max(1, Math.ceil(remaining / days));
  }

  private async getOfflineTrainNextRecommendation(courseId: number): Promise<TrainNextRecommendation | null> {
    if (!isCapacitorNative()) return null;
    const stats = await this.localDb.getOfflineTrainNextStats(courseId).catch(() => null);
    if (!stats) return null;
    const goal = await this.localDb.getLocalCourseGoal(courseId).catch(() => null);
    const goalSetup = this.buildOfflineGoalSetup(stats.totalQuestions, stats.questionsSeen);
    const dailyTarget = {
      plannedQuestions: goal?.dailyQuestionTarget ?? goalSetup.preferredSessionSize,
      answeredToday: 0,
      remainingToday: goal?.dailyQuestionTarget ?? goalSetup.preferredSessionSize,
      onTrack: false,
      streakDay: 0,
    };
    if (!goal) {
      return {
        courseId,
        subjectId: stats.subjectId ?? undefined,
        subjectName: stats.subjectName ?? undefined,
        recommendationType: 'SET_COURSE_GOAL',
        mode: 'test',
        questionCount: 0,
        reason: `Set a course target so ${BRAND.productName} can calculate your daily workload. Offline presets use installed packs.`,
        ctaLabel: 'Set learning target',
        filters: { onlyWrong: false, onlyUnseen: false, excludeHidden: true, topicIds: null },
        dailyTarget,
        readiness: {
          status: stats.subjectSeen > 0 ? 'learning' : 'not_started',
          rollingErrorRate: stats.subjectSeen > 0 ? stats.subjectWrong / stats.subjectSeen : null,
          canStartExam: false,
          questionsSeen: stats.subjectSeen,
          questionsTotal: stats.subjectTotal,
          unseenRemaining: Math.max(0, stats.subjectTotal - stats.subjectSeen),
          dueReviews: stats.subjectWrong,
        },
        goalSetup,
      };
    }
    const dueReviews = stats.subjectWrong;
    const unseen = Math.max(0, stats.subjectTotal - stats.subjectSeen);
    const preferred = Math.max(5, Math.min(100, goal.preferredSessionSize || 20));
    const count = Math.min(preferred, Math.max(1, dueReviews >= 5 ? dueReviews : unseen || preferred));
    const onlyWrong = dueReviews >= 5;
    return {
      courseId,
      subjectId: stats.subjectId ?? undefined,
      subjectName: stats.subjectName ?? undefined,
      recommendationType: onlyWrong ? 'DUE_WRONG_REVIEW' : unseen > 0 ? 'UNSEEN_STUDY' : 'CONTINUE_SUBJECT',
      mode: 'test',
      questionCount: count,
      reason: onlyWrong
        ? `You have ${dueReviews} wrong-answer reviews available offline.`
        : unseen > 0
          ? 'Continue this subject with unseen questions from the installed pack.'
          : 'Keep this subject warm with an offline review session.',
      ctaLabel: onlyWrong ? `Train ${count} wrong questions` : `Train ${count} questions`,
      filters: { onlyWrong, onlyUnseen: !onlyWrong && unseen > 0, excludeHidden: true, topicIds: null },
      dailyTarget,
      readiness: {
        status: stats.subjectSeen > 0 ? 'learning' : 'not_started',
        rollingErrorRate: stats.subjectSeen > 0 ? stats.subjectWrong / stats.subjectSeen : null,
        canStartExam: false,
        questionsSeen: stats.subjectSeen,
        questionsTotal: stats.subjectTotal,
        unseenRemaining: unseen,
        dueReviews,
      },
      goalSetup: null,
    };
  }

  private deriveSessionSize(dailyTarget: number): number {
    return Math.max(5, Math.min(40, dailyTarget));
  }

  private buildOfflineGoalSetup(totalQuestions: number, questionsSeen: number): GoalSetup {
    const remainingQuestions = Math.max(0, totalQuestions - questionsSeen);
    const presets = [
      ['intensive', 'Intensive', 3],
      ['focused', 'Focused', 6],
      ['steady', 'Steady', 9],
      ['balanced', 'Balanced', 12],
      ['light', 'Light', 18],
    ].map(([id, label, months]) => {
      const target = new Date();
      target.setMonth(target.getMonth() + Number(months));
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const days = Math.max(1, Math.ceil((target.getTime() - today.getTime()) / 86_400_000) + 1);
      const dailyQuestions = Math.max(1, Math.ceil(remainingQuestions / days));
      const targetDate = target.toISOString().slice(0, 10);
      return {
        id: String(id),
        label: String(label),
        months: Number(months),
        targetDate,
        dailyQuestions,
        description: `Finish in ${months} months, about ${dailyQuestions} questions/day`,
      };
    });
    const intensiveDaily = presets[0]?.dailyQuestions ?? 20;
    return {
      totalQuestions,
      questionsSeen,
      remainingQuestions,
      preferredSessionSize: this.deriveSessionSize(intensiveDaily),
      weeklyTrainingDays: 7,
      presets,
    };
  }

  /** Resolve API image URL list on a loaded session question (blob URLs for display). */
  private assignResolvedQuestionImages(sessionId: string, questionId: number, apiUrls: string[]): void {
    if (!apiUrls.length) return;
    const entry = this.session.questionsMap[questionId];
    if (!entry) return;
    entry.imageUrls = [...apiUrls];
    this.resolveQuestionImageUrls(sessionId, apiUrls).then(blobUrls => {
      const q = this.session.questionsMap[questionId];
      if (!q) return;
      q.imageUrls = blobUrls;
      q.image = blobUrls[0] ?? null;
    });
  }

  /** Fetch question image with auth and return blob URL for use in img src. Caller should revoke the URL when done. */
  async getQuestionImageBlobUrl(sessionId: string, imageId: number): Promise<string | null> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      const blob = await firstValueFrom(
        this.http.get(`${this.apiUrl}/question-images/${imageId}`, { headers, responseType: 'blob' })
      );
      return URL.createObjectURL(blob);
    } catch (error) {
      LoggerService.error('Failed to fetch question image:', error);
      return null;
    }
  }

  /** Fetch one question DTO by ID (independent of current test state). */
  async getQuestionById(sessionId: string, questionId: number): Promise<Question | null> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      return await firstValueFrom(
        this.http.get<Question>(`${this.apiUrl}/questions/${questionId}`, { headers })
      );
    } catch (error) {
      LoggerService.error('Failed to fetch question by id:', error);
      return null;
    }
  }

  /**
   * Resolve all question stem images to blob URLs (modal / inline question tab).
   */
  async getQuestionImageBlobUrls(sessionId: string, questionId: number): Promise<string[]> {
    try {
      const q = await this.getQuestionById(sessionId, questionId);
      const urls = q?.imageUrls?.length ? q.imageUrls : (q?.image ? [q.image] : []);
      if (!urls.length) return [];
      return await this.resolveQuestionImageUrls(sessionId, urls);
    } catch (error) {
      LoggerService.error('Failed to resolve question images:', error);
      return [];
    }
  }

  /**
   * Resolve the question's primary internal image to a blob URL.
   * Works outside current test context (e.g. search/detail modals).
   */
  async getQuestionPrimaryImageBlobUrl(sessionId: string, questionId: number): Promise<string | null> {
    const urls = await this.getQuestionImageBlobUrls(sessionId, questionId);
    return urls[0] ?? null;
  }

  /** Resolve /api/question-images/X URLs to blob URLs (fetched with auth). */
  async resolveQuestionImageUrls(sessionId: string, urls: string[]): Promise<string[]> {
    const result: string[] = [];
    for (const url of urls || []) {
      const match = String(url).match(/\/api\/question-images\/(\d+)/);
      if (match) {
        const blobUrl = await this.getQuestionImageBlobUrl(sessionId, parseInt(match[1], 10));
        result.push(blobUrl || url);
      } else {
        result.push(url);
      }
    }
    return result;
  }

  async getQuestionExplanation(sessionId: string, questionId: number): Promise<any | null> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      const response = await firstValueFrom(
        this.http.get<any>(`${this.apiUrl}/questions/${questionId}/explanation`, { headers })
      );
      if (response?.explanationImageUrls?.length) {
        response.explanationImageUrls = await this.resolveQuestionImageUrls(sessionId, response.explanationImageUrls);
      }
      if (response?.figureExplanation && String(response.figureExplanation).includes('/api/question-images/')) {
        const resolved = await this.resolveQuestionImageUrls(sessionId, [response.figureExplanation]);
        if (resolved[0]) response.figureExplanation = resolved[0];
      }
      return response;
    } catch (error) {
      LoggerService.error('Failed to get question explanation:', error);
      return null;
    }
  }

  async getQuestionNotes(sessionId: string, questionId: number): Promise<string | null> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      const response = await firstValueFrom(
        this.http.get<any>(`${this.apiUrl}/questions/${questionId}/notes`, { headers })
      );
      return response?.notes || null;
    } catch (error) {
      LoggerService.error('Failed to get question notes:', error);
      return null;
    }
  }

  async saveQuestionNotes(sessionId: string, questionId: number, notes: string): Promise<boolean> {
    if (this.progressSync.shouldDeferToOutbox()) {
      await this.progressSync.enqueueNoteUpdated(questionId, notes);
      return true;
    }
    try {
      const headers = this.getAuthHeaders(sessionId);
      await firstValueFrom(
        this.http.post(`${this.apiUrl}/questions/${questionId}/notes`, { notes }, { headers })
      );
      return true;
    } catch (error) {
      LoggerService.error('Failed to save question notes:', error);
      return false;
    }
  }

  async getQuestionExamState(sessionId: string, questionId: number): Promise<any[] | null> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      const response = await firstValueFrom(
        this.http.get<any[]>(`${this.apiUrl}/questions/${questionId}/exam-state`, { headers })
      );
      return response;
    } catch (error) {
      LoggerService.error('Failed to get question exam state:', error);
      return null;
    }
  }

  async getUserComments(sessionId: string, courseId?: number, subjectId?: number): Promise<any[] | null> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      let url = `${this.apiUrl}/questions/my-comments`;
      const params: string[] = [];
      if (courseId !== undefined) params.push(`courseId=${courseId}`);
      if (subjectId !== undefined) params.push(`subjectId=${subjectId}`);
      if (params.length > 0) url += '?' + params.join('&');
      
      const response = await firstValueFrom(
        this.http.get<any[]>(url, { headers })
      );
      return response || [];
    } catch (error) {
      LoggerService.error('Failed to load user comments:', error);
      return null;
    }
  }

  async getActiveNews(sessionId: string): Promise<{ id: number; headline: string; text: string; createdAt: string }[] | null> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      const response = await firstValueFrom(this.http.get<any>(`${this.apiUrl}/news`, { headers }));
      const arr = response?.content ?? [];
      return Array.isArray(arr) ? arr : null;
    } catch (error) {
      LoggerService.error('Failed to load news:', error);
      return null;
    }
  }

  async getUserNotes(sessionId: string, courseId?: number, subjectId?: number): Promise<any[] | null> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      let url = `${this.apiUrl}/questions/my-notes`;
      const params: string[] = [];
      if (courseId !== undefined) params.push(`courseId=${courseId}`);
      if (subjectId !== undefined) params.push(`subjectId=${subjectId}`);
      if (params.length > 0) url += '?' + params.join('&');
      
      const response = await firstValueFrom(
        this.http.get<any[]>(url, { headers })
      );
      return response || [];
    } catch (error) {
      LoggerService.error('Failed to load user notes:', error);
      return null;
    }
  }

  async getQuestionComments(sessionId: string, questionId: number): Promise<any[] | null> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      const response = await firstValueFrom(
        this.http.get<any[]>(`${this.apiUrl}/questions/${questionId}/comments`, { headers })
      );
      return response;
    } catch (error) {
      LoggerService.error('Failed to get question comments:', error);
      return null;
    }
  }

  async submitQuestionComment(sessionId: string, questionId: number, comment: string): Promise<boolean> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      const response = await firstValueFrom(
        this.http.post<any>(`${this.apiUrl}/questions/${questionId}/comments`, { comment }, { headers })
      );
      return true;
    } catch (error: any) {
      LoggerService.error('Failed to submit question comment:', error);
      if (error.status === 400 && error.error?.error) {
        throw new Error(error.error.error);
      }
      return false;
    }
  }

  async getQuestionState(sessionId: string, questionId: number): Promise<{ flagId: number | null; hide: number } | null> {
    try {
      const headers = this.getAuthHeaders(sessionId);
      const response = await firstValueFrom(
        this.http.get<any>(`${this.apiUrl}/questions/${questionId}/state`, { headers })
      );
      return response;
    } catch (error) {
      LoggerService.error('Failed to get question state:', error);
      return null;
    }
  }

  async saveQuestionState(sessionId: string, questionId: number, flagId: number | null, hide: number): Promise<boolean> {
    if (this.progressSync.shouldDeferToOutbox()) {
      await this.progressSync.enqueueFlagSet(questionId, flagId, hide);
      return true;
    }
    try {
      const headers = this.getAuthHeaders(sessionId);
      LoggerService.log(`Sending saveQuestionState request: questionId=${questionId}, flagId=${flagId}, hide=${hide}`);
      await firstValueFrom(
        this.http.post(`${this.apiUrl}/questions/${questionId}/state`, { flagId, hide }, { headers })
      );
      LoggerService.log(`saveQuestionState request successful`);
      return true;
    } catch (error: any) {
      LoggerService.error('Failed to save question state:', error);
      if (error.error) {
        LoggerService.error('Error details:', error.error);
      }
      if (error.status) {
        LoggerService.error(`HTTP status: ${error.status}`, null);
      }
      return false;
    }
  }
}

export interface UserTestListItem {
  id: number;
  subjectName: string; // first subject for display
  subjectNames?: string[]; // all subjects (for tooltip)
  questionCount: number;
  completedCount: number;
  score: number;
  savedDate: string;
  /** True if test was ended (e.g. user clicked Finish); false if only saved (e.g. Cancel to Resume Later). */
  finished?: boolean;
  mode: string;
  subjectIds?: number[]; // for filter
  shared?: boolean; // true if this test is shared by current user
  sharedByUsername?: string; // when listing shared-with-me: username of sharer
  sharedWithUsername?: string; // when shared=true: username of user this test is shared with
  courseId?: number;
  /** When listing shared-with-me: false if recipient cannot recreate or view the test. */
  accessible?: boolean;
  accessBlockedReason?: string;
}

export interface QuestionAnswerEvent {
  userTestId: number | null;
  questionId: number;
  selectedOption: string | null; // 'A', 'B', 'C', 'D' or null
  correct: boolean;
  examMode: boolean;
  answeredAt: string; // ISO string
}

export interface UserTestResumeResponse {
  userTestId: number;
  courseId: number;
  subjectIds: number[]; // order preserved for recreate
  mode: string;
  amountQuestions?: number;
  redFlagMode?: string;
  yellowFlagMode?: string;
  greenFlagMode?: string;
  excludeHidden?: number;
  onlyUnseen?: number;
  onlyWrong?: number;
  topicIds?: string;
  seedId?: number;
  questions: QuestionAnswerInfo[];
}

/** Filter settings to persist when creating a test (for recreatable tests). */
export interface CreateUserTestSettings {
  amountQuestions?: number;
  redFlagMode?: string;
  yellowFlagMode?: string;
  greenFlagMode?: string;
  excludeHidden?: boolean;
  onlyUnseen?: boolean;
  onlyWrong?: boolean;
  topicIds?: number[];
  seedId?: number;
}

export interface QuestionAnswerInfo {
  questionId: number;
  questionAnswer: string | null; // 'A', 'B', 'C', 'D' or null if not answered
  correct: number | null; // 0 or 1, or null if not answered
  answeredAt: string | null; // null if not answered yet
}


export interface DashboardStatistics {
  questionsSeen: number;
  averageScore: number;
  testsDone?: number;
  xpTotal?: number;
  xpToday?: number;
  xpWeek?: number;
  xpLevel?: number;
  recentMilestones?: DashboardMilestone[];
  nextMilestone?: DashboardMilestone | null;
  lastActiveCourseId?: number | null;
  testHistory: TestPerformance[];
}

export interface DashboardMilestone {
  milestoneId?: number | null;
  type: string;
  title: string;
  description?: string | null;
  courseId?: number | null;
  subjectId?: number | null;
  subjectName?: string | null;
  progress?: number | null;
  threshold?: number | null;
  xpAwarded?: number | null;
  achievedAt?: string | null;
}

export interface TrainNextRecommendation {
  courseId: number;
  subjectId?: number;
  subjectName?: string;
  recommendationType: string;
  mode: string;
  questionCount: number;
  reason: string;
  ctaLabel: string;
  questionIds?: number[];
  filters: {
    onlyWrong: boolean;
    onlyUnseen: boolean;
    excludeHidden: boolean;
    topicIds?: number[] | null;
  };
  dailyTarget: {
    plannedQuestions: number;
    answeredToday: number;
    remainingToday: number;
    onTrack: boolean;
    streakDay: number;
  };
  readiness: {
    status?: string;
    rollingErrorRate?: number | null;
    canStartExam: boolean;
    questionsSeen: number;
    questionsTotal: number;
    unseenRemaining: number;
    dueReviews: number;
  };
  goalSetup?: GoalSetup | null;
}

export interface ResultFeedback {
  userTestId: number;
  courseId: number;
  dailyTarget: TrainNextRecommendation['dailyTarget'];
  nextRecommendation: TrainNextRecommendation;
  xpEarned?: number;
  streakDay?: number;
  dailyGoalMet?: boolean;
  weakTopics?: WeakTopicFeedback[];
  readiness?: ReadinessFeedback;
}

export interface WeakTopicFeedback {
  topicId?: number;
  topicName?: string;
  wrongCount?: number;
}

export interface ReadinessFeedback {
  status?: string;
  subjectName?: string;
  questionsSeen?: number;
  questionsTotal?: number;
  message?: string;
}

export interface GoalSetup {
  totalQuestions: number;
  questionsSeen: number;
  remainingQuestions: number;
  preferredSessionSize: number;
  weeklyTrainingDays: number;
  presets: GoalPreset[];
}

export interface GoalPreset {
  id: string;
  label: string;
  months: number;
  targetDate: string;
  dailyQuestions: number;
  description: string;
}

export interface SaveCourseGoalRequest {
  courseId: number;
  targetDate: string;
  leaderboardOptIn?: boolean;
  weeklyTrainingDays?: number;
  includeWeekends?: boolean;
  preferredSessionSize?: number;
  dailyMinutesTarget?: number;
  reminderTimeLocal?: string | null;
  reminderStyle?: string;
  reminderTimezone?: string;
}

export interface NotificationPreference {
  courseId: number;
  inAppEnabled?: boolean;
  pushEnabled?: boolean;
  emailEnabled?: boolean;
  localNotificationEnabled?: boolean;
  routineReminderEnabled?: boolean;
  saveReminderEnabled?: boolean;
  weeklySummaryEnabled?: boolean;
  quietHoursStart?: string | null;
  quietHoursEnd?: string | null;
}

export interface SaveNotificationPreferenceRequest {
  courseId: number;
  inAppEnabled?: boolean;
  pushEnabled?: boolean;
  emailEnabled?: boolean;
  localNotificationEnabled?: boolean;
  routineReminderEnabled?: boolean;
  saveReminderEnabled?: boolean;
  weeklySummaryEnabled?: boolean;
  quietHoursStart?: string | null;
  quietHoursEnd?: string | null;
}

export interface CourseGoal {
  courseId: number;
  targetDate: string;
  weeklyTrainingDays: number;
  includeWeekends: number;
  dailyQuestionTarget: number;
  preferredSessionSize: number;
  maxDailySuggestedQuestions: number;
  dailyMinutesTarget?: number;
  reminderTimeLocal?: string;
  reminderStyle?: string;
  reminderTimezone?: string;
}

export interface CourseGoalSettings {
  goal?: CourseGoal | null;
  goalSetup: GoalSetup;
  leaderboardOptIn?: boolean | null;
  leaderboardDisplayName?: string | null;
  notificationPreference?: NotificationPreference | null;
}

export interface SocialSummary {
  pendingFriendRequests: number;
  sharedTestsCount: number;
  sharedTestNotificationTitle?: string | null;
  sharedTestNotificationBody?: string | null;
}

export interface RetentionFeatureFlags {
  studyTogetherEnabled: boolean;
  friendRequestsEnabled: boolean;
  testSharingEnabled: boolean;
  leaderboardEnabled: boolean;
  leaderboardOptInDefault: boolean;
}

export interface CoachReminder {
  id: number;
  courseId: number;
  triggerType: string;
  title: string;
  body: string;
  sentAt: string;
}

export interface FriendSummary {
  userId: number;
  displayName: string;
  streakDay?: number;
  trainedToday?: boolean;
}

export interface FriendRequestSummary {
  friendshipId: number;
  userId: number;
  displayName: string;
  direction: 'incoming' | 'outgoing';
}

export interface FriendSuggestion {
  userId: number;
  displayName: string;
  countryName?: string | null;
  authorityName?: string | null;
  reason?: string;
}

export interface LeaderboardEntryRow {
  rank: number;
  displayName: string;
  xp: number;
  viewer?: boolean;
}

export interface LeaderboardClimb {
  trigger: string;
  previousRank?: number | null;
  currentRank?: number | null;
  delta?: number | null;
  headline: string;
}

export interface LeaderboardResponse {
  periodId?: number;
  startsAt: string;
  endsAt: string;
  metric: string;
  optedIn?: boolean;
  viewerRank?: number | null;
  viewerXp?: number | null;
  viewerDisplayName?: string | null;
  entries: LeaderboardEntryRow[];
  totalParticipants: number;
  climb?: LeaderboardClimb | null;
}

export interface TestPerformance {
  date: string;
  subjectName: string;
  score: number;
  questionCount: number;
  subjectId: number;
  mode: string;
}

